import React, { useState, useEffect, useRef } from 'react';
import { supabase, SUPABASE_PUBLISHABLE_KEY } from '../lib/supabase';
import { fetchWithTimeout, bookSearchHeaders, SEARCH_BOX_TIMEOUT_MS, publicationYear } from '../lib/bookSearch';
import { getCurrentUser } from '../lib/auth';
import { fetchMyPostState } from '../lib/postState';
import { todayInPacific, onlyUpcoming, isUpcoming } from '../lib/eventDates';
import { useToast } from '../components/Toast';
import {
  usePosts,
  useLiveActivity,
  useTrendingTropes,
  useBookClubs,
  useTasteMatches,
  useCommunityStats,
} from '../hooks/useCommunity';
// BookPickerModal defined below — no external import needed
import Add5StarReadPopup from '../components/community/Add5StarReadPopup';
import { resolveAllTagsToBookTags, tagNameError, writeReaderGenrePicks } from '../lib/tropeUtils';
import ReportModal, { KebabMenu, KebabItem } from '../components/community/ReportModal';
import { createPortal } from 'react-dom';
import { useGuestGate } from '../hooks/useGuestGate';
import SignUpGate from '../components/SignUpGate';
import ComingSoon, { COMING_SOON_COLOURS } from '../components/shared/ComingSoon';
import { onCoverError } from '../lib/coverFallback';
import { findOrCreateBook } from '../lib/findOrCreateBook';
import { useGenreTaxonomy } from '../hooks/useGenreTaxonomy';
import GenreSubgenrePicker from '../components/genre/GenreSubgenrePicker';
// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────




function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── useIsMobile ──────────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED POST SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function TapeDecoration({ dark }) {
  return (
    <div style={{
      position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)',
      width: 50, height: 19,
      background: dark ? 'rgba(68,60,52,0.7)' : 'rgba(253,233,170,0.8)',
      borderRadius: 3,
      border: dark ? '1px solid rgba(100,88,72,0.4)' : '1px solid rgba(210,168,72,0.4)',
      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
      zIndex: 1,
    }} />
  );
}

function Badge({ label, bg, color }) {
  return (
    <span style={{
      fontSize: 'var(--font-size-micro)', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
      padding: '3px 7px', borderRadius: 'var(--radius-sm)', background: bg, color,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {label}
    </span>
  );
}
import { useNavigate } from 'react-router-dom'; // add this at the top if not already there

function UserHeader({ post, dark, badge, onProfileClick }) {
  const [showReport, setShowReport] = useState(false);
  const navigate = useNavigate();

const nameColor = dark ? '#faf8f5' : '#292524';
  const timeColor = dark ? '#78716c' : '#a8a29e';
  const avatarBg  = dark ? '#44403c' : '#f0ebe3';

  function goToProfile(e) {
    e.stopPropagation();
    if (!post.username) return;
    onProfileClick
      ? onProfileClick(() => navigate(`/u/${post.username}`))
      : navigate(`/u/${post.username}`);
  }

 

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-10)', width: '100%' }}>

        {/* Avatar — clickable */}
        <div
          onClick={goToProfile}
          title={`View @${post.username}'s profile`}
          style={{
            width: 28, height: 28, borderRadius: 'var(--radius-circle)', flexShrink: 0,
            background: post.avatar_url ? `url(${post.avatar_url}) center/cover` : avatarBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 'var(--font-size-xs)', fontWeight: 700, color: dark ? 'var(--neutral-400)' : 'var(--neutral-500)',
            overflow: 'hidden',
            cursor: post.username ? 'pointer' : 'default',
          }}
        >
          {!post.avatar_url && (post.username?.[0]?.toUpperCase() || 'U')}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Username — clickable */}
          <span
            onClick={goToProfile}
            style={{
              fontSize: 'var(--font-size-sm)', fontWeight: 700, color: nameColor,
              cursor: post.username ? 'pointer' : 'default',
              textDecoration: 'none',
            }}
            onMouseEnter={e => { if (post.username) e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
          >
            @{post.username || 'reader'}
          </span>
          <span style={{ fontSize: 'var(--font-size-xxs)', color: timeColor, marginLeft: 'var(--space-6)' }}>
            {formatTimeAgo(post.created_at)}
          </span>
          {post.ephemeral && (
            <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning)', marginLeft: 'var(--space-6)' }}>⏳ {post.ephemeral}</span>
          )}
        </div>

        {badge && badge}
        <KebabMenu dark={dark}>
          <KebabItem onClick={() => setShowReport(true)} danger>🚩 Report</KebabItem>
        </KebabMenu>
      </div>

      {showReport && createPortal(
        <ReportModal entityType="post" entityId={post.id} reportedUserId={post.user_id} onClose={() => setShowReport(false)} />,
        document.body
      )}
    </>
  );
}

function PostActions({ post, dark }) {
  const postId = post.id; // bigint — never stringify this

  // Count comes from posts.likes_count — already loaded, always public
  const [likeCount,    setLikeCount]    = useState(post.likes_count || 0);
  const [liked,        setLiked]        = useState(false);
  const [liking,       setLiking]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments,     setComments]     = useState([]);
  const [commentText,  setCommentText]  = useState('');
  const [commentCount, setCommentCount] = useState(post.comments_count || 0);
  const [posting,      setPosting]      = useState(false);

  // ── Only check if current user liked/saved this post ──────────────────────
  // Batched across every card on screen — see lib/postState.js. Asking per
  // card produced two requests per post (an N+1 on /community).
useEffect(() => {
  let alive = true;
  (async () => {
    const { liked: isLiked, saved: isSaved } = await fetchMyPostState(postId);
    if (!alive) return;
    setLiked(isLiked);
    setSaved(isSaved);
  })();
  return () => { alive = false; };
}, [postId]);

// Realtime: live comments for all viewers
useEffect(() => {
  const channel = supabase
    .channel(`post_comments:${postId}`)
    .on(
      'postgres_changes',
      {
        event:  'INSERT',
        schema: 'public',
        table:  'post_comments',
        filter: `post_id=eq.${String(postId)}`,
      },
      async (payload) => {
        const newComment = payload.new;
        if (!newComment) return;

        // Fetch the commenter's profile
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('username, avatar_url')
          .eq('user_id', newComment.user_id)
          .maybeSingle();

        setComments(prev => {
          // Avoid duplicates (in case current user just submitted)
          if (prev.some(c => c.id === newComment.id)) return prev;
          return [...prev, {
            ...newComment,
            username:   profile?.username   || 'reader',
            avatar_url: profile?.avatar_url || null,
          }];
        });
        setCommentCount(c => c + 1);
      }
    )
    .subscribe();

  return () => { channel.unsubscribe(); };
}, [postId]);

  // ── Realtime: watch posts.likes_count for this post ───────────────────────
  // Trigger updates posts.likes_count → all subscribers see the new count
  useEffect(() => {
    const channel = supabase
      .channel(`post-count:${postId}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'posts', filter: `id=eq.${postId}` },
        (payload) => {
          if (payload.new?.likes_count != null) {
            setLikeCount(payload.new.likes_count);
          }
        }
      )
      .subscribe();
    return () => { channel.unsubscribe(); };
  }, [postId]);

  // ── Toggle like ───────────────────────────────────────────────────────────
  async function handleLike() {
    if (liking) return;
    setLiking(true);

    const user = await getCurrentUser();
    if (!user) { setLiking(false); return; }

    // Optimistic update
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount(c => wasLiked ? Math.max(0, c - 1) : c + 1);

    if (wasLiked) {
      const { error } = await supabase.from('post_likes').delete()
        .eq('post_id', postId)
        .eq('user_id', user.id);
      if (error) {
        console.error('unlike error:', error.message);
        setLiked(true);
        setLikeCount(c => c + 1);
      }
    } else {
      const { error } = await supabase.from('post_likes')
        .upsert(
          { post_id: postId, user_id: user.id },
          { onConflict: 'post_id,user_id', ignoreDuplicates: true }
        );
      if (error) {
        console.error('like error:', error.message);
        setLiked(false);
        setLikeCount(c => Math.max(0, c - 1));
      }
    }

    setLiking(false);
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  async function handleSave() {
    try {
      const user = await getCurrentUser();
      if (!user) return;
      if (saved) {
        setSaved(false);
        const { error } = await supabase.from('saved_posts').delete()
          .eq('user_id', user.id).eq('post_id', postId);
        if (error) setSaved(true);
      } else {
        setSaved(true);
        const { error } = await supabase.from('saved_posts')
          .insert({ user_id: user.id, post_id: postId });
        if (error) setSaved(false);
      }
    } catch {}
  }

  // ── Comments ──────────────────────────────────────────────────────────────
useEffect(() => {
  // Always load the count on mount so it shows correctly without opening panel
  (async () => {
    try {
      const { count } = await supabase
        .from('post_comments')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', String(postId));
      if (count != null) setCommentCount(count);
    } catch {}
  })();
}, [postId]);

useEffect(() => {
  if (!showComments) return;
  let alive = true;
  (async () => {
    try {
      const { data } = await supabase
        .from('post_comments')
        .select('id, content, created_at, user_id')
        .eq('post_id', String(postId))
        .order('created_at', { ascending: true }).limit(50);
      if (!alive || !data) return;

      const ids = [...new Set(data.map(c => c.user_id))];
      const map = {};
      if (ids.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles').select('user_id, username, avatar_url')
          .in('user_id', ids);
        (profiles || []).forEach(p => { map[p.user_id] = p; });
      }
      setComments(data.map(c => ({
        ...c,
        username:   map[c.user_id]?.username   || 'reader',
        avatar_url: map[c.user_id]?.avatar_url || null,
      })));
      setCommentCount(data.length);
    } catch {}
  })();
  return () => { alive = false; };
}, [showComments, postId]);

// Realtime: new comments appear instantly for all viewers
useEffect(() => {
  const channel = supabase
    .channel(`post_comments:${postId}`)
    .on('postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'post_comments', filter: `post_id=eq.${String(postId)}` },
      async (payload) => {
        if (!payload.new) return;
        const { data: profile } = await supabase
          .from('user_profiles').select('username, avatar_url')
          .eq('user_id', payload.new.user_id).maybeSingle();
        setComments(prev => {
          if (prev.some(c => c.id === payload.new.id)) return prev;
          return [...prev, {
            ...payload.new,
            username:   profile?.username   || 'reader',
            avatar_url: profile?.avatar_url || null,
          }];
        });
        setCommentCount(c => c + 1);
      }
    )
    .subscribe();
  return () => { channel.unsubscribe(); };
}, [postId]);

 async function submitComment() {
  if (!commentText.trim() || posting) return;
  try {
    const user = await getCurrentUser();
    if (!user) return;
    setPosting(true);
    const { data, error } = await supabase.from('post_comments')
      .insert({ post_id: String(postId), user_id: user.id, content: commentText.trim() })
      .select('id, content, created_at, user_id').single();
    if (!error && data) {
      const { data: profile } = await supabase
        .from('user_profiles').select('username, avatar_url')
        .eq('user_id', user.id).maybeSingle();
      // Only add to local state if realtime hasn't already added it
      setComments(prev => {
        if (prev.some(c => c.id === data.id)) return prev;
        return [...prev, {
          ...data,
          username:   profile?.username   || 'reader',
          avatar_url: profile?.avatar_url || null,
        }];
      });
      setCommentCount(c => c + 1);
      setCommentText('');
    }
  } catch {}
  finally { setPosting(false); }
}

  // ── Render ────────────────────────────────────────────────────────────────
  const textColor   = dark ? '#78716c' : '#a8a29e';
  const borderColor = dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:'var(--space-14)', paddingTop:'var(--space-10)', marginTop:'var(--space-10)', borderTop:`1px solid ${borderColor}` }}>
        <button onClick={handleLike} disabled={liking}
          style={{ background:'none', border:'none', cursor: liking ? 'wait' : 'pointer',
            fontSize:'var(--font-size-base)', color: liked ? 'var(--feedback-error)' : textColor,
            display:'flex', alignItems:'center', gap:'var(--space-xs)', padding:'var(--space-none)',
            transition:'color 0.15s', opacity: liking ? 0.7 : 1 }}>
          {liked ? '❤️' : '🤍'} <span style={{ fontSize:'var(--font-size-sm)' }}>{likeCount}</span>
        </button>
        <button onClick={() => setShowComments(p => !p)}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:'var(--font-size-base)',
            color: showComments ? 'var(--feedback-warning)' : textColor,
            display:'flex', alignItems:'center', gap:'var(--space-xs)', padding:'var(--space-none)', transition:'color 0.15s' }}>
          💬 <span style={{ fontSize:'var(--font-size-sm)' }}>{commentCount}</span>
        </button>
        <button onClick={handleSave}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:'var(--font-size-base)',
            color: saved ? 'var(--feedback-warning)' : textColor,
            marginLeft:'auto', padding:'var(--space-none)', transition:'color 0.15s' }}>
          {saved ? '🔖' : '🔗'}
        </button>
      </div>

      {showComments && (
        <div style={{ marginTop:'var(--space-10)', borderTop:`1px solid ${borderColor}`, paddingTop:'var(--space-10)' }}>
          {comments.length === 0 && (
            <p style={{ fontSize:'var(--font-size-xs)', color:textColor, margin:'0 0 10px', fontStyle:'italic' }}>
              No comments yet, be the first!
            </p>
          )}
          {comments.map(c => (
            <div key={c.id} style={{ display:'flex', gap:'var(--space-sm)', marginBottom:'var(--space-sm)' }}>
              <div style={{ width:24, height:24, borderRadius:'var(--radius-circle)', flexShrink:0,
                background: c.avatar_url ? `url(${c.avatar_url}) center/cover` : (dark ? 'var(--neutral-700)' : '#f0ebe3'),
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'var(--font-size-xxs)', fontWeight:700, color: dark ? 'var(--neutral-400)' : 'var(--neutral-500)', overflow:'hidden' }}>
                {!c.avatar_url && c.username[0]?.toUpperCase()}
              </div>
              <div style={{ flex:1, background: dark ? 'rgba(255,255,255,0.06)' : 'var(--neutral-100)', borderRadius:'var(--radius-md)', padding:'6px 10px' }}>
                <span style={{ fontSize:'var(--font-size-xs)', fontWeight:700, color: dark ? 'var(--brand-paper)' : 'var(--neutral-800)', marginRight:'var(--space-6)' }}>@{c.username}</span>
                <span style={{ fontSize:'var(--font-size-xs)', color: dark ? 'var(--neutral-300)' : 'var(--neutral-600)' }}>{c.content}</span>
              </div>
            </div>
          ))}
          <div style={{ display:'flex', gap:'var(--space-sm)', marginTop:'var(--space-xs)' }}>
            <input value={commentText} onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); }}}
              placeholder="Write a comment..."
              style={{ flex:1, padding:'7px 10px',
                background: dark ? 'rgba(255,255,255,0.08)' : 'var(--neutral-100)',
                border: dark ? '1px solid rgba(255,255,255,0.12)' : '1px solid var(--brand-sand)',
                borderRadius:'var(--radius-md)', fontSize:'var(--font-size-sm)', color: dark ? 'var(--brand-paper)' : 'var(--neutral-800)', outline:'none' }} />
            <button onClick={submitComment} disabled={!commentText.trim() || posting}
              style={{ padding:'7px 14px', borderRadius:'var(--radius-md)', border:'none', fontSize:'var(--font-size-sm)', fontWeight:700,
                cursor: commentText.trim() ? 'pointer' : 'default',
                background: commentText.trim() ? 'var(--feedback-warning)' : (dark ? 'rgba(255,255,255,0.1)' : 'var(--brand-sand)'),
                color: commentText.trim() ? 'var(--brand-white)' : textColor, transition:'all 0.15s' }}>
              {posting ? '...' : 'Post'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
 
 
 
 

const STICKY_BASE = {
  background: '#fef9ec',
  borderRadius: 'var(--radius-sm)',
  padding: '24px 18px 16px',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
  border: '1px solid rgba(210,168,72,0.28)',
};



// ─────────────────────────────────────────────────────────────────────────────

function ProfileThoughtCard({ post, rotation, onProfileClick  }) {
  return (
    <div style={{ ...STICKY_BASE, transform: `rotate(${rotation}deg)` }}>
      <TapeDecoration />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-sm)' }}>
        <UserHeader post={post} dark={false} badge={<Badge label="FROM PROFILE" bg="#fde68a" color="#92400e" />} />
      </div>
      <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 'var(--font-size-md)', color: 'var(--neutral-700)', lineHeight: 1.7, margin: '0 0 4px' }}>
        {post.content}
      </p>
      <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>from their profile · {formatTimeAgo(post.created_at)}</p>
      <PostActions post={post} dark={false} />
    </div>
  );
}

function DirectThoughtCard({ post, rotation, onProfileClick }) {
  return (
    <div style={{ ...STICKY_BASE, transform: `rotate(${rotation}deg)` }}>
      <TapeDecoration />
      <UserHeader post={post} dark={false} onProfileClick={onProfileClick} />
      <p style={{ fontFamily: 'Georgia, serif', fontSize: 'var(--font-size-md)', color: 'var(--neutral-700)', lineHeight: 1.7, margin: '0 0 8px' }}>
        {post.content}
      </p>
      <PostActions post={post} dark={false} />
    </div>
  );
}

function HotTakeCard({ post, rotation, onProfileClick  }) {
  return (
    <div style={{
      background: 'var(--neutral-800)', borderRadius: 'var(--radius-sm)', padding: '24px 18px 16px',
      position: 'relative', transform: `rotate(${rotation}deg)`,
      boxShadow: '0 2px 16px rgba(220,38,38,0.14), 0 0 0 1px rgba(220,38,38,0.1)',
      overflow: 'hidden',
    }}>
      <TapeDecoration dark />
      {/* Red glow */}
      <div style={{ position: 'absolute', top: 0, right: 0, width: 70, height: 70, background: 'radial-gradient(circle, rgba(220,38,38,0.28) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-10)' }}>
       <UserHeader post={post} dark={false} badge={<Badge label="HOT TAKE" bg="#dc2626" color="#fff" />} onProfileClick={onProfileClick}/>
      </div>
      <p style={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: 15, color: 'var(--brand-paper)', lineHeight: 1.65, margin: '0 0 8px' }}>
        {post.content}
      </p>
      <PostActions post={post} dark />
    </div>
  );
}

function ReadingUpdateCard({ post, rotation, onProfileClick  }) {
  // Reanlo DB book (from join) takes priority; fall back to inline metadata for external books
  const book = post.books || post.event_data?.book_meta || null;
  return (
    <div style={{ ...STICKY_BASE, transform: `rotate(${rotation}deg)` }}>
      <TapeDecoration />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-10)' }}>
        <UserHeader post={post} dark={false} badge={<Badge label="READING UPDATE" bg="#dbeafe" color="#1e40af" />} onProfileClick={onProfileClick} />
      </div>
      {book && (
        <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-10)', padding: 'var(--space-10)', display: 'flex', gap: 'var(--space-10)', marginBottom: 'var(--space-10)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid var(--neutral-100)' }}>
          <div style={{ width: 44, height: 64, borderRadius: 6, flexShrink: 0, overflow: 'hidden', background: 'linear-gradient(135deg, var(--feedback-warning-border), #f59e0b)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {book.cover_image_url
              ? <img src={book.cover_image_url} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
              : <span style={{ fontSize: 'var(--font-size-xl)' }}>📚</span>}
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px' }}>{book.title}</p>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{book.author_name}</p>
          </div>
        </div>
      )}
      {post.content && (
        <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 'var(--font-size-base)', color: 'var(--neutral-700)', lineHeight: 1.65, margin: '0 0 8px' }}>
          "{post.content}"
        </p>
      )}
      <PostActions post={post} dark={false} />
    </div>
  );
}
function PollCard({ post, rotation, onProfileClick  }) {
  const postIdStr = String(post.id);
  const options   = post.poll_options || [];

  const [voteCounts, setVoteCounts] = useState(options.map(() => 0));
  const [votedIndex, setVotedIndex] = useState(null);
  const [loading,    setLoading]    = useState(true);

  const totalVotes = voteCounts.reduce((sum, c) => sum + c, 0);

  // ── Load ALL votes on mount ───────────────────────────────────────────────
useEffect(() => {
  let alive = true;

  async function loadVotes() {
    try {
      const user = await getCurrentUser();

    const { data: votes, error } = await supabase
  .from('poll_votes')
  .select('user_id, option_index')
  .eq('post_id', postIdStr);

console.log('poll query:', { postIdStr, votes, error });

      if (!alive) return;

      if (error) {
        console.error('poll load error:', error.message);
        setLoading(false);
        return;
      }

      const counts = options.map((_, i) =>
        (votes || []).filter(v => v.option_index === i).length
      );
      setVoteCounts(counts);

      if (user) {
        const mine = (votes || []).find(v => v.user_id === user.id);
        setVotedIndex(mine ? mine.option_index : null);
      }
    } catch (e) {
      console.error('poll load exception:', e.message);
    } finally {
      if (alive) setLoading(false);
    }
  }

  loadVotes();
  return () => { alive = false; };
}, [postIdStr]);

  // ── Realtime: new votes from any user ────────────────────────────────────
 useEffect(() => {
  const channel = supabase
    .channel(`poll:${postIdStr}`)
    .on('postgres_changes',
      {
        event:  'INSERT',
        schema: 'public',
        table:  'poll_votes',
        filter: `post_id=eq.${postIdStr}`,
      },
      (payload) => {
        const idx = payload.new?.option_index;
        if (idx == null) return;
        // This handles count for ALL users including the voter
        setVoteCounts(prev => prev.map((c, i) => i === idx ? c + 1 : c));
      }
    )
    .subscribe();

  return () => { channel.unsubscribe(); };
}, [postIdStr]);

  // ── Vote ──────────────────────────────────────────────────────────────────
async function handleVote(i) {
  if (votedIndex !== null) return;

  const user = await getCurrentUser();
  if (!user) return;

  // Check DB first for stale state
  const { data: existing } = await supabase
    .from('poll_votes')
    .select('option_index')
    .eq('post_id', postIdStr)
    .eq('user_id', user.id)
    .maybeSingle();

  if (existing) {
    setVotedIndex(existing.option_index);
    return;
  }

  // Only lock the UI — let realtime handle the count increment
  setVotedIndex(i);

  const { error } = await supabase.from('poll_votes')
    .upsert(
      { post_id: postIdStr, user_id: user.id, option_index: i },
      { onConflict: 'post_id,user_id', ignoreDuplicates: true }
    );

  if (error) {
    // Revert on real error
    setVotedIndex(null);
    console.error('vote error:', error.message);
  }
}

  // ── Render ────────────────────────────────────────────────────────────────
  const hasVoted = votedIndex !== null;

  return (
    <div style={{ ...STICKY_BASE, transform: `rotate(${rotation}deg)` }}>
      <TapeDecoration />
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'var(--space-10)' }}>
        <UserHeader post={post} dark={false} badge={<Badge label="POLL" bg="#f0fdf4" color="#166534" />} onProfileClick={onProfileClick}/>
      </div>
      <p style={{ fontFamily:'Georgia, serif', fontSize:'var(--font-size-md)', fontWeight:600, color:'var(--neutral-800)', margin:'0 0 12px', lineHeight:1.5 }}>
        {post.content}
      </p>

      {loading ? (
        <div style={{ padding:'12px 0', textAlign:'center' }}>
          <div style={{ width:18, height:18, border:'2px solid var(--brand-sand)', borderTopColor:'var(--feedback-warning)', borderRadius:'var(--radius-circle)', animation:'spin 0.8s linear infinite', display:'inline-block' }} />
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
          {options.map((opt, i) => {
            const count    = voteCounts[i] || 0;
            const pct      = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
            const isMyVote = votedIndex === i;
            return (
              <button key={i} onClick={() => handleVote(i)} disabled={hasVoted}
                style={{
                  width:'100%', padding:'9px 12px', borderRadius:'var(--radius-md)',
                  border:`1.5px solid ${isMyVote ? '#d97706' : '#e8e4dc'}`,
                  background: hasVoted ? 'transparent' : 'var(--brand-white)',
                  cursor: hasVoted ? 'default' : 'pointer',
                  position:'relative', overflow:'hidden', textAlign:'left',
                }}>
                {hasVoted && (
                  <div style={{
                    position:'absolute', left:0, top:0, height:'100%', width:`${pct}%`,
                    background: isMyVote ? 'rgba(217,119,6,0.13)' : 'rgba(0,0,0,0.04)',
                    transition:'width 0.5s ease',
                  }} />
                )}
                <span style={{ position:'relative', fontSize:'var(--font-size-base)', fontWeight:500, color:'var(--neutral-800)' }}>
                  {isMyVote && '✓ '}{opt.text}
                </span>
                {hasVoted && (
                  <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', fontSize:'var(--font-size-sm)', fontWeight:700, color:'var(--neutral-500)' }}>
                    {pct}% · {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <p style={{ fontSize:'var(--font-size-xxs)', color:'var(--neutral-400)', margin:'8px 0 4px', textAlign:'right' }}>
        {totalVotes} vote{totalVotes !== 1 ? 's' : ''}{hasVoted ? ' · voted' : ''}
      </p>
      <PostActions post={post} dark={false} />
    </div>
  );
}
 
 

function SpoilerCard({ post, rotation, onProfileClick  }) {
  const [revealed, setRevealed] = useState(false);

  if (!revealed) {
    return (
      <div style={{ background: 'var(--neutral-800)', borderRadius: 'var(--radius-sm)', padding: '24px 18px 16px', position: 'relative', transform: `rotate(${rotation}deg)`, boxShadow: '0 2px 12px rgba(0,0,0,0.18)' }}>
        <TapeDecoration dark />
        <UserHeader post={post} dark />
        <div style={{ textAlign: 'center', padding: '14px 0 10px' }}>
          <div style={{ fontSize: 30, marginBottom: 'var(--space-sm)' }}>🚨</div>
          {post.books && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '0 0 14px' }}>Spoiler: {post.books.title}</p>}
          <button
            onClick={() => setRevealed(true)}
            style={{ padding: '8px 24px', background: 'var(--feedback-error)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer' }}
          >
            Reveal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...STICKY_BASE, transform: `rotate(${rotation}deg)`, animation: 'fadeIn 0.3s ease' }}>
      <TapeDecoration />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-sm)' }}>
        <UserHeader post={post} dark={false} badge={<Badge label="SPOILER" bg="#dc2626" color="#fff" />} onProfileClick={onProfileClick}/>
      </div>
      <p style={{ fontFamily: 'Georgia, serif', fontSize: 'var(--font-size-md)', color: 'var(--neutral-700)', lineHeight: 1.7, margin: '0 0 8px' }}>
        {post.content}
      </p>
      <PostActions post={post} dark={false} />
    </div>
  );
}

function SharedShelfCard({ post, rotation, onProfileClick  }) {
  const shelf = post.shelf_data;
  return (
    <div style={{ ...STICKY_BASE, transform: `rotate(${rotation}deg)` }}>
      <TapeDecoration />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-10)' }}>
        <UserHeader post={post} dark={false} badge={<Badge label="SHARED SHELF" bg="#f0fdf4" color="#166534" />} onProfileClick={onProfileClick} />
        
      </div>
      {shelf && (
        <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-10)', padding: 'var(--space-md)', marginBottom: 'var(--space-10)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px' }}>
            {shelf.emoji} {shelf.name}
          </p>
          <div style={{ display: 'flex', gap: 5 }}>
            {(shelf.books || []).slice(0, 6).map((b, i) => (
              <div key={i} style={{ width: 34, height: 50, borderRadius: 'var(--radius-sm)', overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(135deg, var(--feedback-warning-border), #f59e0b)' }}>
                {b.cover_image_url && <img src={b.cover_image_url} alt={b.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />}
              </div>
            ))}
          </div>
        </div>
      )}
      <button style={{ fontSize: 'var(--font-size-sm)', color: 'var(--brand-forest)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--space-none)' }}>
        + Save to my shelves
      </button>
      <PostActions post={post} dark={false} />
    </div>
  );
}

function ImagePostCard({ post, rotation, onProfileClick  }) {
  return (
    <div style={{ ...STICKY_BASE, transform: `rotate(${rotation}deg)` }}>
      <TapeDecoration />
      <UserHeader post={post} dark={false} />
      {post.image_url && (
        <div style={{ borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: 'var(--space-10)' }}>
          <img src={post.image_url} alt="post" style={{ width: '100%', display: 'block' }} />
        </div>
      )}
      {post.content && (
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-700)', lineHeight: 1.6, margin: '0 0 8px' }}>{post.content}</p>
      )}
      <PostActions post={post} dark={false} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EVENT CARD COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const EVENT_ROLE_META = {
  hosting:    { verb: 'is hosting',       icon: '📣', badgeBg: '#B85C38', badgeColor: '#fff',    badgeLabel: 'Hosting'    },
  going:      { verb: 'is going to',      icon: '🙋', badgeBg: '#ecfdf5', badgeColor: '#059669', badgeLabel: 'Going'      },
  interested: { verb: 'is interested in', icon: '👀', badgeBg: '#f5f5f4', badgeColor: '#78716c', badgeLabel: 'Interested' },
};

function TicketStub({ ev }) {
  const color = ev.color || '#2D4A3E';
  const dateParts = (ev.date || '').split(' ');
  return (
    <div style={{ display: 'flex', overflow: 'hidden', borderRadius: 'var(--radius-10)', border: '1px solid rgba(0,0,0,0.07)', background: 'var(--brand-white)', marginBottom: 'var(--space-10)' }}>
      {/* Coloured date column */}
      <div style={{
        background: `linear-gradient(145deg, ${color}, ${color}bb)`,
        padding: '10px 13px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, minWidth: 58, position: 'relative',
      }}>
        {/* Perforated tear dots */}
        <div style={{ position: 'absolute', right: -1, top: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
          {[0,1,2,3,4].map(d => (
            <div key={d} style={{ width: 8, height: 8, borderRadius: 'var(--radius-circle)', background: 'var(--brand-paper)', marginRight: -4 }} />
          ))}
        </div>
        <span style={{ fontSize: 'var(--font-size-xl)', marginBottom: 'var(--space-xs)' }}>{ev.emoji || '📅'}</span>
        {dateParts[0] && (
          <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 800, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {dateParts[0]}
          </span>
        )}
        {dateParts[1] && (
          <span style={{ fontSize: 19, fontWeight: 900, color: 'var(--brand-white)', lineHeight: 1.1 }}>{dateParts[1]}</span>
        )}
        {!dateParts[1] && <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>TBA</span>}
      </div>
      {/* Event details */}
      <div style={{ padding: '10px 14px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 3px', lineHeight: 1.3 }}>
          {ev.title || 'Community Event'}
        </p>
        <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: '0 0 2px' }}>
          {[ev.time, ev.location].filter(Boolean).join(' · ')}
        </p>
        {ev.city && <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>{ev.city}</p>}
      </div>
    </div>
  );
}
function EventCard({ post, rotation, onProfileClick }) {
  const [eventRsvp, setEventRsvp]             = useState(null);
  const [rsvpLoading, setRsvpLoading]         = useState(false);
  const [goingCount, setGoingCount]           = useState(0);
  const [interestedCount, setInterestedCount] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [myProfileEventId, setMyProfileEventId] = useState(null);

  const navigate = useNavigate();

  const raw = post.event_data || {};
  const ev = {
    title:            raw.title    || post.content?.split('\n')[0] || 'Community Event',
    date:             raw.date     || '',
    time:             raw.time     || '',
    location:         raw.location || raw.venue || '',
    city:             raw.city     || '',
    emoji:            raw.emoji    || '📅',
    color:            raw.color    || 'var(--brand-rust)',
    role:             raw.role     || 'going',
    desc:             raw.desc     || (raw.title ? post.content : '') || '',
    lat:              raw.lat      || null,
    lng:              raw.lng      || null,
    profile_event_id: raw.profile_event_id || null,
  };

  const meta       = EVENT_ROLE_META[ev.role] || EVENT_ROLE_META.going;
  const bg         = rotation >= 0 ? '#fffbeb' : '#fef7ed';
  const postIdStr  = String(post.id);

  // ── Flag the profile_event directly + insert a report ────────────────────
  async function handleFlag() {
    try {
      const user = await getCurrentUser();
      if (!user) { alert('Please log in to report events.'); return; }

      // Insert into reports table so it shows in admin Reports section
      await supabase.from('reports').insert({
        entity_type:      'event',
        entity_id:        ev.profile_event_id ? String(ev.profile_event_id) : postIdStr,
        reported_user_id: post.user_id || null,
        reporter_id:      user.id,
        reason:           'Reported by community member',
        status:           'open',
      });

      // Also flag the profile_event directly so it shows in admin Events section
      if (ev.profile_event_id) {
        const { data: current } = await supabase
          .from('profile_events')
          .select('flag_count')
          .eq('id', ev.profile_event_id)
          .maybeSingle();

        await supabase.from('profile_events').update({
          flagged:      true,
          flag_count:   (current?.flag_count || 0) + 1,
          flag_reason:  'Reported by community member',
        }).eq('id', ev.profile_event_id);
      }

      setShowReport(false);
      alert('Event reported. Our team will review it shortly.');
    } catch (err) {
      alert('Failed to report: ' + err.message);
    }
  }

  useEffect(() => {
 async function loadRsvpState() {
  const user = await getCurrentUser();
 
  const { data: allRsvps } = await supabase
    .from('event_rsvps')
    .select('status, user_id')
    .eq('post_id', postIdStr);
 
  if (allRsvps) {
    setGoingCount(allRsvps.filter(r => r.status === 'going').length);
    setInterestedCount(allRsvps.filter(r => r.status === 'interested').length);
    if (user) {
      const mine = allRsvps.find(r => r.user_id === user.id);
      if (mine) setEventRsvp(mine.status);
    }
  }
 
  // Find the viewer's own profile_events entry for this event (created on RSVP)
  // so we can delete it if they later un-RSVP.
  if (user && ev.title) {
    const { data: existingPE } = await supabase
      .from('profile_events')
      .select('id, role')
      .eq('user_id', user.id)
      .eq('name', ev.title)
      .in('role', ['going', 'interested', 'attending'])
      .maybeSingle();
    if (existingPE) setMyProfileEventId(existingPE.id);
  }
}
    loadRsvpState();
  }, [postIdStr]);

async function handleRsvp(type) {
  const user = await getCurrentUser();
  if (!user || rsvpLoading) return;
  setRsvpLoading(true);
 
  const next = eventRsvp === type ? null : type;
  const prev = eventRsvp;
 
  // Optimistic UI update
  setEventRsvp(next);
  if (prev === 'going')      setGoingCount(c => Math.max(0, c - 1));
  if (prev === 'interested') setInterestedCount(c => Math.max(0, c - 1));
  if (next === 'going')      setGoingCount(c => c + 1);
  if (next === 'interested') setInterestedCount(c => c + 1);
 
  try {
    // 1. Remove existing event_rsvps row
    await supabase.from('event_rsvps').delete()
      .eq('user_id', user.id)
      .eq('post_id', postIdStr);
 
    // 2. Remove viewer's profile_events row from previous RSVP
    if (myProfileEventId) {
      await supabase.from('profile_events').delete()
        .eq('id', myProfileEventId)
        .eq('user_id', user.id);
      setMyProfileEventId(null);
    }
 
    if (next !== null) {
      // 3. Write new event_rsvps row
      await supabase.from('event_rsvps').insert({
        user_id:          user.id,
        post_id:          postIdStr,
        profile_event_id: ev.profile_event_id ? String(ev.profile_event_id) : null,
        status:           next,
      });
 
      // 4. Create profile_events row for viewer — this makes it appear in
      //    their EventsNearMeBlock on their profile page with the right label.
      //    ev.date here is the raw ISO date stored in event_data when posted.
      const { data: inserted, error: peErr } = await supabase
        .from('profile_events')
        .insert({
          user_id:     user.id,
          name:        ev.title    || 'Community Event',
          date:        ev.date     || null,    // raw "YYYY-MM-DD" from event_data
          time:        ev.time     || null,
          venue:       ev.location || null,
          address:     ev.city     || null,
          description: ev.desc     || null,
          role:        next,                   // 'going' or 'interested'
          emoji:       ev.emoji    || '🎟️',
          color:       ev.color    || 'var(--brand-forest)',
          lat:         ev.lat      || null,
          lng:         ev.lng      || null,
        })
        .select().single();
 
      if (peErr) {
        console.error('[EventCard] profile_events insert failed:', peErr.message);
      } else if (inserted) {
        setMyProfileEventId(inserted.id);
      }
    }
  } catch (err) {
    console.error('RSVP error:', err.message);
    // Revert optimistic update
    setEventRsvp(prev);
    if (prev === 'going')      setGoingCount(c => c + 1);
    if (prev === 'interested') setInterestedCount(c => c + 1);
    if (next === 'going')      setGoingCount(c => Math.max(0, c - 1));
    if (next === 'interested') setInterestedCount(c => Math.max(0, c - 1));
  }
 
  setRsvpLoading(false);
}
 

  return (
    <>
      <div style={{
        background: bg, borderRadius: 6, padding: '14px 18px',
        border: '1px solid var(--feedback-warning-border)', position: 'relative',
        transform: `rotate(${rotation}deg)`,
        boxShadow: '0 1px 10px rgba(251,191,36,0.08)',
      }}>
        <div style={{
          position: 'absolute', top: -4,
          left: rotation >= 0 ? 16 : 'auto',
          right: rotation < 0  ? 16 : 'auto',
          width: 32, height: 7,
          background: 'linear-gradient(90deg, rgba(217,119,6,0.14), rgba(217,119,6,0.22))',
          borderRadius: 2, transform: `rotate(${rotation >= 0 ? -2 : 2.5}deg)`,
        }} />

        {/* ── Header row with flag button ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-10)', width: '100%' }}>
          <div style={{
            width: 26, height: 26, borderRadius: 'var(--radius-circle)', flexShrink: 0, overflow: 'hidden',
            background: post.avatar_url ? `url(${post.avatar_url}) center/cover` : 'var(--brand-cream)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)',
          }}>
            {!post.avatar_url && (post.username?.[0]?.toUpperCase() || 'U')}
          </div>





<p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-800)', margin: 'var(--space-none)', flex: 1, lineHeight: 1.3 }}>
  <span
  onClick={() => {
    if (!post.username) return;
    onProfileClick
      ? onProfileClick(() => navigate(`/u/${post.username}`))
      : navigate(`/u/${post.username}`);
  }}
    style={{
      fontWeight: 700,
      cursor: post.username ? 'pointer' : 'default',
    }}
    onMouseEnter={e => { if (post.username) e.currentTarget.style.textDecoration = 'underline'; }}
    onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
  >
    @{post.username || 'reader'}
  </span>{' '}
  <span style={{ color: 'var(--neutral-500)' }}>{meta.verb}</span>
</p>

          <span style={{ fontSize: 'var(--font-size-micro)', padding: '3px 8px', borderRadius: 6, flexShrink: 0, whiteSpace: 'nowrap', background: meta.badgeBg, color: meta.badgeColor, fontWeight: 700 }}>
            {meta.icon} {meta.badgeLabel}
          </span>

          {/* Flag button */}
          <button
            onClick={() => setShowReport(true)}
            title="Report this event"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 'var(--font-size-base)', padding: '2px 4px', borderRadius: 6,
              color: 'var(--neutral-400)', flexShrink: 0,
              transition: 'color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.background = '#fee2e2'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#a8a29e'; e.currentTarget.style.background = 'none'; }}
          >
            🚩
          </button>
        </div>

        <TicketStub ev={ev} />

        {ev.desc && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-600)', margin: '0 0 10px', lineHeight: 1.5 }}>{ev.desc}</p>
        )}

        <div style={{ display: 'flex', gap: 'var(--space-6)', marginBottom: 'var(--space-10)' }}>
          <button onClick={() => handleRsvp('going')} disabled={rsvpLoading}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-xs)', padding: 'var(--space-sm)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600, transition: 'all 0.15s', background: eventRsvp === 'going' ? 'var(--neutral-800)' : 'var(--brand-white)', color: eventRsvp === 'going' ? 'var(--brand-paper)' : 'var(--neutral-800)', border: eventRsvp === 'going' ? 'none' : '1px solid var(--brand-sand)', opacity: rsvpLoading ? 0.6 : 1 }}>
            {eventRsvp === 'going' ? '✓ ' : '🙋 '}Going
            <span style={{ fontSize: 'var(--font-size-tiny)', opacity: 0.7, marginLeft: 'var(--space-xxs)' }}>{goingCount}</span>
          </button>
          <button onClick={() => handleRsvp('interested')} disabled={rsvpLoading}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-xs)', padding: 'var(--space-sm)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600, transition: 'all 0.15s', background: eventRsvp === 'interested' ? 'var(--feedback-warning-bg)' : 'var(--brand-white)', color: eventRsvp === 'interested' ? 'var(--feedback-warning-ink)' : 'var(--neutral-500)', border: eventRsvp === 'interested' ? '1.5px solid var(--feedback-warning)' : '1px solid var(--brand-sand)', opacity: rsvpLoading ? 0.6 : 1 }}>
            {eventRsvp === 'interested' ? '✓ ' : '👀 '}Interested
            <span style={{ fontSize: 'var(--font-size-tiny)', opacity: 0.7, marginLeft: 'var(--space-xxs)' }}>{interestedCount}</span>
          </button>
        </div>

        <PostActions post={post} dark={false} />
      </div>

      {/* Confirm report dialog */}
      {showReport && createPortal(
        <div
          onClick={() => setShowReport(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(28,25,23,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 380, width: '100%',
              padding: '28px 26px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            fontFamily: 'var(--font-body)',
            }}
          >
            <div style={{ fontSize: 'var(--font-size-h2)', marginBottom: 'var(--space-md)', textAlign: 'center' }}>🚩</div>
            <h3 style={{ fontFamily: 'Georgia, serif', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--neutral-900)', margin: '0 0 8px', textAlign: 'center' }}>
              Report this event?
            </h3>
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 6px', lineHeight: 1.5, textAlign: 'center' }}>
              <strong style={{ color: 'var(--neutral-800)' }}>{ev.title}</strong>
            </p>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '0 0 22px', textAlign: 'center', lineHeight: 1.5 }}>
              Our moderation team will review this event and take action if it violates community guidelines.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button
                onClick={() => setShowReport(false)}
                style={{ flex: 1, padding: 'var(--space-10)', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: 'var(--neutral-600)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleFlag}
                style={{ flex: 1, padding: 'var(--space-10)', borderRadius: 'var(--radius-10)', border: 'none', background: 'var(--feedback-error)', color: 'var(--brand-white)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer' }}
              >
                🚩 Report event
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EVENTS NEAR ME — REAL LEAFLET MAP WIDGET
// Loads Leaflet from CDN (no npm install needed). Pins are real lat/lng coords.
// ─────────────────────────────────────────────────────────────────────────────

const DEMO_EVENTS = [
  { id: 'e1', username: '@bookitwithehss', role: 'hosting',    title: 'Toronto Book Swap',          date: 'Apr 19', time: '2:00 PM',  location: 'Trinity Bellwoods Park',  city: 'Toronto, ON',    emoji: '📚', color: 'var(--brand-rust)', attendees: 23,  interested: 8,   lat: 43.6426, lng: -79.4187, desc: 'Bring books you love and swap with other readers!' },
  { id: 'e2', username: '@romantasy_rebel',role: 'going',      title: 'Poetry Night at NYPL',       date: 'Apr 24', time: '7:00 PM',  location: 'New York Public Library', city: 'New York, NY',   emoji: '🎤', color: 'var(--brand-forest)', attendees: 156, interested: 42,  lat: 40.7532, lng: -73.9822, desc: 'Monthly poetry open mic night. All skill levels welcome.' },
  { id: 'e3', username: '@cozy_bookworm',  role: 'interested', title: 'SJM Midnight Release',       date: 'May 1',  time: '11:00 PM', location: 'Barnes & Noble Union Sq', city: 'New York, NY',   emoji: '🌙', color: '#7c3aed', attendees: 89,  interested: 134, lat: 40.7369, lng: -73.9903, desc: 'Midnight release party. Costumes encouraged!' },
  { id: 'e4', username: '@dark_academia',  role: 'hosting',    title: 'Dark Romance Book Club IRL', date: 'May 5',  time: '6:00 PM',  location: 'The Last Bookstore',      city: 'Los Angeles, CA',emoji: '🖤', color: 'var(--neutral-900)', attendees: 18,  interested: 27,  lat: 34.0507, lng: -118.2500,desc: 'Monthly dark romance discussion. Controversial opinions welcome.' },
  { id: 'e5', username: '@spicy_pages',    role: 'going',      title: 'Indie Author Meet & Greet',  date: 'May 8',  time: '3:00 PM',  location: 'The Strand Bookstore',    city: 'New York, NY',   emoji: '✍️', color: 'var(--feedback-warning)', attendees: 42,  interested: 19,  lat: 40.7335, lng: -73.9910, desc: 'Meet 12 indie romance and fantasy authors. Book signings & giveaways.' },
  { id: 'e6', username: '@tbr_mountain',   role: 'interested', title: 'Bookish Brunch',             date: 'May 12', time: '11:00 AM', location: 'Cafe Lalo',               city: 'New York, NY',   emoji: '☕', color: 'var(--feedback-success)', attendees: 15,  interested: 38,  lat: 40.7843, lng: -73.9755, desc: "Casual brunch for readers. Bring what you're currently reading!" },
];

// ─────────────────────────────────────────────────────────────────────────────
// EVENTS NEAR ME — Fixed version
// Changes:
//   1. Role badge in upcoming list now shows the CREATOR's relationship to the
//      event, not the viewer's RSVP — labelled as "Hosted by poster" etc.
//   2. RSVP counts + current user's status are loaded from DB on mount and
//      refreshed when a pin is selected.
//   3. Pin popup shows real going/interested counts and lets the viewer toggle.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// EVENTS NEAR ME — Fixed version
// Changes:
//   1. Role badge in upcoming list now shows the CREATOR's relationship to the
//      event, not the viewer's RSVP — labelled as "Hosted by poster" etc.
//   2. RSVP counts + current user's status are loaded from DB on mount and
//      refreshed when a pin is selected.
//   3. Pin popup shows real going/interested counts and lets the viewer toggle.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// EVENTS NEAR ME — Fixed version
// Changes:
//   1. Role badge in upcoming list now shows the CREATOR's relationship to the
//      event, not the viewer's RSVP — labelled as "Hosted by poster" etc.
//   2. RSVP counts + current user's status are loaded from DB on mount and
//      refreshed when a pin is selected.
//   3. Pin popup shows real going/interested counts and lets the viewer toggle.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// EVENTS NEAR ME — Fixed version
// Changes:
//   1. Role badge in upcoming list now shows the CREATOR's relationship to the
//      event, not the viewer's RSVP — labelled as "Hosted by poster" etc.
//   2. RSVP counts + current user's status are loaded from DB on mount and
//      refreshed when a pin is selected.
//   3. Pin popup shows real going/interested counts and lets the viewer toggle.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// EVENTS NEAR ME — Fixed version
// Changes:
//   1. Role badge in upcoming list now shows the CREATOR's relationship to the
//      event, not the viewer's RSVP — labelled as "Hosted by poster" etc.
//   2. RSVP counts + current user's status are loaded from DB on mount and
//      refreshed when a pin is selected.
//   3. Pin popup shows real going/interested counts and lets the viewer toggle.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// EVENTS NEAR ME — Fixed version
// Changes:
//   1. Role badge in upcoming list now shows the CREATOR's relationship to the
//      event, not the viewer's RSVP — labelled as "Hosted by poster" etc.
//   2. RSVP counts + current user's status are loaded from DB on mount and
//      refreshed when a pin is selected.
//   3. Pin popup shows real going/interested counts and lets the viewer toggle.
// ─────────────────────────────────────────────────────────────────────────────

function EventsNearMeWidget({ events: passedEvents }) {
  const mapElRef      = useRef(null);
  const leafletRef    = useRef(null);
  const markersRef    = useRef([]);
  const [leafletReady, setLeafletReady] = useState(!!window.L);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [expanded, setExpanded]          = useState(false);
  const [mapReady, setMapReady]          = useState(false);

  // ── RSVP state — keyed by profile_event id ────────────────────────────────
  // { [eventId]: { going: number, interested: number, myRsvp: 'going'|'interested'|null } }
  const [rsvpData, setRsvpData] = useState({});
  const [rsvpLoading, setRsvpLoading] = useState(false);

  const listEvents = passedEvents || [];
  const mapEvents  = listEvents.filter(e => e.lat && e.lng);

  // ── Load Leaflet ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }
    if (!document.querySelector('#leaflet-css')) {
      const link  = document.createElement('link');
      link.id     = 'leaflet-css';
      link.rel    = 'stylesheet';
      link.href   = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (!document.querySelector('#leaflet-js')) {
      const script    = document.createElement('script');
      script.id       = 'leaflet-js';
      script.src      = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload   = () => setLeafletReady(true);
      document.head.appendChild(script);
    }
    return () => {
      if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; }
    };
  }, []);

  // ── Init map ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!leafletReady || !mapElRef.current || leafletRef.current) return;
    const L   = window.L;
    const map = L.map(mapElRef.current, {
      center: [20, 0], zoom: 2,
      zoomControl: false, scrollWheelZoom: false, attributionControl: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, opacity: 0.88 }).addTo(map);
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    leafletRef.current = map;
    setMapReady(true);
  }, [leafletReady]);

  // ── Place markers ─────────────────────────────────────────────────────────
  const mapEventsKey = mapEvents.map(e => `${e.id}:${e.lat},${e.lng}`).join('|');
  useEffect(() => {
    if (!leafletReady || !leafletRef.current || mapEvents.length === 0) return;
    const L   = window.L;
    const map = leafletRef.current;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    const groups = [];
    mapEvents.forEach(ev => {
      const g = groups.find(g => Math.abs(g.lat - ev.lat) < 0.6 && Math.abs(g.lng - ev.lng) < 0.6);
      if (g) g.items.push(ev);
      else groups.push({ lat: ev.lat, lng: ev.lng, items: [ev] });
    });

    groups.forEach(group => {
      const isCluster = group.items.length > 1;
      const first     = group.items[0];

      // ── Calendar icon — shows the actual date instead of a generic emoji ──
      // Clusters show a count badge; single events show a mini calendar with
      // the month strip in the event colour and the day number below.
      let iconHtml, iconW, iconH;

      if (isCluster) {
        // Multi-event cluster — coloured circle with count
        iconW = 28; iconH = 28;
        iconHtml = `
          <div style="
            width:28px;height:28px;border-radius:50%;
            background:#2D4A3E;border:2px solid #fff;
            box-shadow:0 2px 8px rgba(0,0,0,0.25);
            display:flex;align-items:center;justify-content:center;cursor:pointer;
          ">
            <span style="font-size:11px;font-weight:900;color:#fff;line-height:1">
              ${group.items.length}
            </span>
          </div>`;
      } else {
        // Single event — teardrop pin with day number
        const dateParts = (first.date || '').split(' ');
        const day   = dateParts[1] || '?';
        const color = first.color || '#B85C38';

        iconW = 26; iconH = 30;
        iconHtml = `
          <div style="width:26px;height:30px;position:relative;cursor:pointer;">
            <div style="
              position:absolute;top:0;left:0;
              width:26px;height:26px;
              background:${color};
              border-radius:50% 50% 50% 0;
              transform:rotate(-45deg);
              border:2.5px solid #fff;
              box-shadow:0 2px 8px rgba(0,0,0,0.22);
              display:flex;align-items:center;justify-content:center;
            ">
              <span style="
                transform:rotate(45deg);
                font-size:10px;font-weight:800;
                color:#fff;font-family:system-ui,sans-serif;line-height:1;
              ">${day}</span>
            </div>
          </div>`;
      }

      const icon = L.divIcon({
        className: '',
        html: iconHtml,
        iconSize:   [iconW, iconH],
        iconAnchor: [4, 28],  // tip of the teardrop
      });

      const marker = L.marker([group.lat, group.lng], { icon }).addTo(map);
      marker.on('click', () => {
        setSelectedGroup(group.items);
        map.panTo([group.lat, group.lng]);
      });
      markersRef.current.push(marker);
    });

    if (groups.length > 0) {
      const bounds = L.latLngBounds(groups.map(g => [g.lat, g.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
    }
    setTimeout(() => map.invalidateSize(), 200);
  }, [mapEventsKey, leafletReady, mapReady]);

  useEffect(() => {
    if (leafletRef.current) setTimeout(() => leafletRef.current?.invalidateSize(), 320);
  }, [expanded]);

  // ── Load RSVP data for all events on mount ────────────────────────────────
  // Loads: going count, interested count, and current user's RSVP status per event.
  // This is what the pin popup uses to show real numbers and the viewer's state.
  useEffect(() => {
    if (!listEvents.length) return;
    let alive = true;
    loadRsvpData(listEvents.map(e => e.id)).then(data => {
      if (alive) setRsvpData(data);
    });
    return () => { alive = false; };
  }, [listEvents.length]);

  async function loadRsvpData(eventIds) {
    if (!eventIds.length) return {};
    const result = {};
    for (const id of eventIds) result[id] = { going: 0, interested: 0, myRsvp: null, myProfileEventId: null };
    try {
      const user = await getCurrentUser();

      // 1. Load RSVP counts + current user's status for all host events
      const { data: allRsvps } = await supabase
        .from('event_rsvps')
        .select('profile_event_id, status, user_id')
        .in('profile_event_id', eventIds.map(String));

      for (const id of eventIds) {
        const evRsvps = (allRsvps || []).filter(r => String(r.profile_event_id) === String(id));
        const mine    = user ? evRsvps.find(r => r.user_id === user.id) : null;
        result[id] = {
          going:           evRsvps.filter(r => r.status === 'going').length,
          interested:      evRsvps.filter(r => r.status === 'interested').length,
          myRsvp:          mine?.status || null,
          myProfileEventId: null, // filled in below
        };
      }

      // 2. If logged in, also find the viewer's own profile_events rows so we
      //    can delete them when they un-RSVP, and so the block shows correctly
      //    on page reload (match by name since we don't have a direct FK).
      if (user) {
        const { data: viewerEvents } = await supabase
          .from('profile_events')
          .select('id, name, date, role')
          .eq('user_id', user.id)
          .in('role', ['going', 'interested', 'attending']);

        // Build a lookup: lowercase name → viewer profile_events row
        const viewerByName = {};
        (viewerEvents || []).forEach(ve => {
          viewerByName[ve.name?.toLowerCase()] = ve;
        });

        // Match each host event to the viewer's profile_events row
        for (const ev of listEvents) {
          if (!result[ev.id]?.myRsvp) continue;
          const key   = (ev.title || ev.name || '').toLowerCase();
          const match = viewerByName[key];
          if (match) result[ev.id].myProfileEventId = match.id;
        }
      }

      return result;
    } catch (err) {
      console.error('loadRsvpData error:', err.message);
      return result;
    }
  }

  // ── Toggle RSVP ────────────────────────────────────────────────────────────
  // Writing to both event_rsvps (for counts) AND profile_events (so the
  // viewer's EventsNearMeBlock on their profile shows the event with its label).
  async function toggleRsvp(eventId, type) {
    const user = await getCurrentUser();
    if (!user) { alert('Please log in to RSVP to events.'); return; }

    const current = rsvpData[eventId] || { going: 0, interested: 0, myRsvp: null, myProfileEventId: null };
    const next    = current.myRsvp === type ? null : type;
    const prev    = current.myRsvp;

    // Optimistic update
    setRsvpData(old => ({
      ...old,
      [eventId]: {
        ...current,
        going:      current.going      + (prev === 'going'      ? -1 : 0) + (next === 'going'      ? 1 : 0),
        interested: current.interested + (prev === 'interested' ? -1 : 0) + (next === 'interested' ? 1 : 0),
        myRsvp:     next,
        myProfileEventId: next === null ? null : current.myProfileEventId,
      },
    }));

    try {
      // Find event details from the list we already have
      const ev = listEvents.find(e => e.id === eventId);

      // ── Step 1: clean up previous RSVP ────────────────────────────────────
      // Delete from event_rsvps
      await supabase.from('event_rsvps').delete()
        .eq('user_id', user.id)
        .eq('profile_event_id', String(eventId));

      // Delete the viewer's profile_events row that was created by the previous RSVP
      if (current.myProfileEventId) {
        await supabase.from('profile_events').delete()
          .eq('id', current.myProfileEventId)
          .eq('user_id', user.id);
      }

      // ── Step 2: create new RSVP if needed ─────────────────────────────────
      if (next !== null && ev) {
        // ev.date is already formatted as "Apr 19" for display — we need the
        // raw ISO date ("2026-04-19") to insert into profile_events. Fetch it
        // directly from the DB using the host's profile_events id.
        let rawDate = null;
        const { data: rawEv } = await supabase
          .from('profile_events')
          .select('date')
          .eq('id', eventId)
          .maybeSingle();
        rawDate = rawEv?.date || null;

        const { data: inserted, error: insertErr } = await supabase
          .from('profile_events')
          .insert({
            user_id:     user.id,
            name:        ev.title || ev.name || 'Community Event',
            date:        rawDate,                         // raw ISO date for DB
            time:        ev.time  || null,
            venue:       ev.location || ev.venue || null,
            address:     ev.city  || ev.address  || null,
            description: ev.desc  || null,
            role:        next,                            // 'going' or 'interested'
            emoji:       ev.emoji || '🎟️',
            color:       ev.color || 'var(--brand-forest)',
            lat:         ev.lat   || null,
            lng:         ev.lng   || null,
          })
          .select().single();

        if (insertErr) {
          console.error('[EventsNearMeWidget] profile_events insert failed:', insertErr.message);
        }

        // Write to event_rsvps for count queries
        const { error: rsvpErr } = await supabase.from('event_rsvps').insert({
          user_id:          user.id,
          profile_event_id: String(eventId),
          status:           next,
        });
        if (rsvpErr) {
          console.error('[EventsNearMeWidget] event_rsvps insert failed:', rsvpErr.message);
        }

        // Store the viewer's new profile_events ID so we can delete it on un-RSVP
        if (inserted) {
          setRsvpData(old => ({
            ...old,
            [eventId]: { ...old[eventId], myProfileEventId: inserted.id },
          }));
        }
      }
    } catch (err) {
      console.error('toggleRsvp error:', err.message);
      // Revert on error
      const fresh = await loadRsvpData([eventId]);
      setRsvpData(old => ({ ...old, ...fresh }));
    }
  }

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', overflow: 'hidden', border: '1px solid var(--brand-sand)' }}>

      {/* Header */}
      <div style={{ padding: '14px 18px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--neutral-100)' }}>
        <div>
          <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>🗺️ Bookish Events</p>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '2px 0 0' }}>
            {listEvents.length > 0 ? `${listEvents.length} upcoming near you` : 'Be the first to post an event!'}
          </p>
        </div>
        <button
          onClick={() => { setExpanded(p => !p); setSelectedGroup(null); }}
          style={{ fontSize: 'var(--font-size-sm)', color: 'var(--brand-rust)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
        >
          {expanded ? 'Collapse' : 'See all'}
        </button>
      </div>

      {/* Map */}
      <div
        ref={mapElRef}
        style={{ height: expanded ? 280 : 200, transition: 'height 0.3s ease', background: '#e8f0e4', position: 'relative', zIndex: 0 }}
      />

      {/* ── Selected pin panel ────────────────────────────────────────────── */}
      {selectedGroup && (
        <div style={{ borderTop: '1px solid var(--neutral-100)', background: '#fffbeb', animation: 'fadeIn 0.2s ease' }}>
          <div style={{ padding: '10px 16px 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>
              {selectedGroup.length > 1 ? `${selectedGroup.length} events here` : selectedGroup[0].title}
            </p>
            <button
              onClick={() => setSelectedGroup(null)}
              style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', cursor: 'pointer', color: 'var(--neutral-400)', padding: 'var(--space-none)', lineHeight: 1 }}
            >
              ×
            </button>
          </div>

          {selectedGroup.map((ev, i) => {
            const rsvp    = rsvpData[ev.id] || { going: 0, interested: 0, myRsvp: null };
            const myRsvp  = rsvp.myRsvp;

            return (
              <div key={ev.id} style={{ padding: '10px 16px', borderTop: i > 0 ? '1px solid rgba(0,0,0,0.04)' : 'none' }}>
                {/* Event summary */}
                <div style={{ display: 'flex', gap: 'var(--space-10)', alignItems: 'flex-start', marginBottom: 'var(--space-10)' }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 'var(--radius-10)', flexShrink: 0,
                    background: `linear-gradient(135deg, ${ev.color}, ${ev.color}cc)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xl)',
                  }}>
                    {ev.emoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px' }}>{ev.title}</p>
                    <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: '0 0 1px' }}>
                      {ev.date}{ev.time ? ` · ${ev.time}` : ''}
                    </p>
                    <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>
                      📍 {ev.location || ev.city}
                    </p>
                  </div>
                </div>

                {/* RSVP counts */}
                <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-sm)' }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
                    🙋 <strong style={{ color: 'var(--neutral-800)' }}>{rsvp.going}</strong> going
                  </span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
                    👀 <strong style={{ color: 'var(--neutral-800)' }}>{rsvp.interested}</strong> interested
                  </span>
                </div>

                {/* RSVP buttons — viewer's own response */}
                <div style={{ display: 'flex', gap: 'var(--space-6)' }}>
                  <button
                    onClick={() => toggleRsvp(ev.id, 'going')}
                    style={{
                      flex: 1, padding: 'var(--space-sm)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s',
                      background: myRsvp === 'going' ? 'var(--neutral-800)' : 'var(--brand-white)',
                      color:      myRsvp === 'going' ? 'var(--brand-white)'    : 'var(--neutral-800)',
                      border:     myRsvp === 'going' ? 'none'    : '1px solid var(--brand-sand)',
                    }}
                  >
                    {myRsvp === 'going' ? '✓ Going' : '🙋 Going'}
                  </button>
                  <button
                    onClick={() => toggleRsvp(ev.id, 'interested')}
                    style={{
                      flex: 1, padding: 'var(--space-sm)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s',
                      background: myRsvp === 'interested' ? 'var(--feedback-warning-bg)' : 'var(--brand-white)',
                      color:      myRsvp === 'interested' ? 'var(--feedback-warning-ink)' : 'var(--neutral-500)',
                      border:     myRsvp === 'interested' ? '1.5px solid var(--feedback-warning)' : '1px solid var(--brand-sand)',
                    }}
                  >
                    {myRsvp === 'interested' ? '✓ Interested' : '👀 Interested'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Upcoming list ────────────────────────────────────────────────── */}
      {!selectedGroup && listEvents.length > 0 && (
        <div style={{ padding: '10px 18px 6px' }}>
          <p style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 800, color: 'var(--neutral-400)', letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 8px' }}>
            Upcoming · tap to RSVP
          </p>

          {listEvents.slice(0, expanded ? listEvents.length : 3).map((ev, i) => {
            const rsvp   = rsvpData[ev.id] || { going: 0, interested: 0, myRsvp: null };
            const myRsvp = rsvp.myRsvp;

            // Only show a badge if the viewer has already RSVPed or is the host
            const isHost  = ev.role === 'hosting';
            const badge   = isHost
              ? { label: '📣 Hosting', bg: '#B85C38', color: 'var(--brand-white)' }
              : myRsvp === 'going'
              ? { label: '✓ Going',      bg: '#292524', color: 'var(--brand-white)' }
              : myRsvp === 'interested'
              ? { label: '✓ Interested', bg: '#fef3c7', color: 'var(--feedback-warning-ink)' }
              : null; // no badge if viewer hasn't interacted

            return (
              <div
                key={ev.id}
                onClick={() => setSelectedGroup([ev])}
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-10)',
                  padding: '8px 6px', borderTop: i > 0 ? '1px solid var(--neutral-100)' : 'none',
                  cursor: 'pointer', borderRadius: 'var(--radius-md)', transition: 'background 0.12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#faf8f5'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Emoji icon */}
                <div style={{
                  width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                  background: `linear-gradient(135deg, ${ev.color}, ${ev.color}cc)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)',
                }}>
                  {ev.emoji}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ev.title}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 'var(--space-xxs)', flexWrap: 'wrap' }}>
                    <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>
                      {ev.date}{ev.city ? ` · ${ev.city}` : ev.location ? ` · ${ev.location}` : ''}
                    </p>
                    {(rsvp.going > 0 || rsvp.interested > 0) && (
                      <span style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)' }}>
                        · {rsvp.going} going{rsvp.interested > 0 ? ` · ${rsvp.interested} interested` : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* Badge — only if user has RSVPed or is the host */}
                {badge ? (
                  <span style={{
                    fontSize: 'var(--font-size-tiny)', padding: '3px 8px', borderRadius: 6, flexShrink: 0,
                    fontWeight: 700, whiteSpace: 'nowrap',
                    background: badge.bg, color: badge.color,
                  }}>
                    {badge.label}
                  </span>
                ) : (
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-300)', flexShrink: 0 }}>›</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!selectedGroup && listEvents.length === 0 && (
        <div style={{ padding: '20px 18px', textAlign: 'center' }}>
          <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', margin: '0 0 4px' }}>No upcoming events yet</p>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-300)', margin: 'var(--space-none)' }}>Add an event from your profile to drop a pin here</p>
        </div>
      )}

      {/* Legend */}
      <div style={{ padding: '8px 18px', borderTop: '1px solid var(--neutral-100)', display: 'flex', gap: 'var(--space-14)', alignItems: 'center' }}>
        {[
          { c: '#B85C38', l: 'Hosting' },
          { c: '#2D4A3E', l: 'Cluster' },
          { c: '#292524', l: 'Single event' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
            <div style={{ width: 8, height: 8, borderRadius: 'var(--radius-circle)', background: item.c, flexShrink: 0 }} />
            <span style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)' }}>{item.l}</span>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div style={{ padding: '12px 18px 16px', borderTop: '1px solid var(--neutral-100)', background: 'var(--brand-paper)' }}>
        <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 10px' }}>How it works</p>
        {[
          'Reader adds event to their profile (Ticket Stub block)',
          'Event auto-posts as a card in the Community feed',
          'Event drops a pin on this map widget',
          'Other readers RSVP from the feed card OR the map',
        ].map((step, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)', marginBottom: i < 3 ? 7 : 0 }}>
            <div style={{ width: 18, height: 18, borderRadius: 'var(--radius-circle)', background: 'var(--neutral-800)', color: 'var(--brand-white)', fontSize: 'var(--font-size-tiny)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
              {i + 1}
            </div>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)', lineHeight: 1.5 }}>{step}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ServiceUpdateCard({ post, rotation, onProfileClick  }) {
  const services = post.services || [];
  return (
    <div style={{ background: '#1f3d30', borderRadius: 'var(--radius-sm)', padding: '24px 18px 16px', position: 'relative', transform: `rotate(${rotation}deg)`, boxShadow: '0 2px 14px rgba(0,0,0,0.22)' }}>
      <TapeDecoration dark />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-10)' }}>
       <UserHeader post={post} dark badge={ <Badge label="NEW SERVICE" bg="#fef3c7" color="#92400e" />} onProfileClick={onProfileClick} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)', marginBottom: 'var(--space-10)' }}>
        {services.map((s, i) => (
          <span key={i} style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.1)', color: 'var(--neutral-300)', borderRadius: 'var(--radius-xl)', fontSize: 'var(--font-size-sm)' }}>
            {s}
          </span>
        ))}
      </div>
      {post.content && <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-300)', lineHeight: 1.6, margin: '0 0 8px' }}>{post.content}</p>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-14)', paddingTop: 'var(--space-10)', marginTop: 'var(--space-6)', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', padding: 'var(--space-none)' }}>🤍 {post.likes_count || 0}</button>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', padding: 'var(--space-none)' }}>💬 {post.comments_count || 0}</button>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: '#86efac', marginLeft: 'auto', padding: 'var(--space-none)' }}>
          Inquire →
        </button>
      </div>
    </div>
  );
}

function ShopUpdateCard({ post, rotation, onProfileClick  }) {
  const product = post.product_data || {};
  const hasLink = !!product.link;

  const inner = (
    <div style={{ ...STICKY_BASE, transform: `rotate(${rotation}deg)` }}>
      <TapeDecoration />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-10)' }}>
       <UserHeader post={post} dark={false} badge={<Badge label="SHOP" bg="#fef3c7" color="#92400e" />} onProfileClick={onProfileClick} />

      </div>

      {/* Product card */}
      <div style={{
        background: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', overflow: 'hidden',
        marginBottom: 'var(--space-10)', border: '1px solid #f0f0ef',
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.15s',
        ...(hasLink ? { cursor: 'pointer' } : {}),
      }}
        onMouseEnter={e => hasLink && (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)')}
        onMouseLeave={e => hasLink && (e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)')}
      >
        {/* Product image */}
        {product.image_url ? (
          <div style={{ width: '100%', height: 140, overflow: 'hidden', background: 'var(--neutral-100)' }}>
            <img src={product.image_url} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ) : (
          <div style={{ width: '100%', height: 80, background: 'linear-gradient(135deg, var(--feedback-warning-bg), var(--feedback-warning-border))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>
            {product.emoji || '🛍️'}
          </div>
        )}

        {/* Product details */}
        <div style={{ padding: '10px 12px' }}>
          <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px', lineHeight: 1.3 }}>{product.name || 'Product'}</p>
          {product.price && (
            <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 800, color: 'var(--brand-rust)', margin: '0 0 8px' }}>
              {String(product.price).startsWith('$') ? product.price : `$${product.price}`}
            </p>
          )}
          {hasLink && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning)', fontWeight: 700 }}>
              <span>Shop Now</span>
              <span>↗</span>
            </div>
          )}
        </div>
      </div>

      {post.content && (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 8px', lineHeight: 1.55 }}>{post.content}</p>
      )}
      <PostActions post={post} dark={false} />
    </div>
  );

  // Wrap in anchor if there's a link so the whole card is clickable
  if (hasLink) {
    return (
      <a
        href={product.link.startsWith('http') ? product.link : `https://${product.link}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none', display: 'block' }}
      >
        {inner}
      </a>
    );
  }
  return inner;
}

// Subtle sticky-note rotation — alternates per card index
const rotations = [0.3, -0.25, 0.15, -0.3, 0.2, -0.1, 0.28, -0.18];

function renderPostCard(post, index, onProfileClick) {
  const rotation = rotations[index % rotations.length];

  let resolvedType = post.post_type;
  // Legacy: posts saved as 'text' before migration
  if (resolvedType === 'text') {
    if (post.poll_options?.length > 0) resolvedType = 'poll';
    else if (post.image_url)           resolvedType = 'image';
  }

  switch (resolvedType) {
    case 'profile_thought':   return <ProfileThoughtCard  key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    case 'direct_thought':    return <DirectThoughtCard   key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    case 'hot_take':          return <HotTakeCard          key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    case 'reading_update':    return <ReadingUpdateCard    key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    case 'poll':              return <PollCard             key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    case 'spoiler':           return <SpoilerCard          key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    case 'shared_shelf':      return <SharedShelfCard      key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    case 'image':             return <ImagePostCard        key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    case 'event':             return <EventCard            key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    case 'service_update':
    case 'ask_me_about':      return <ServiceUpdateCard    key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    case 'shop_update':       return <ShopUpdateCard       key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
    default:                  return <DirectThoughtCard    key={post.id} post={post} rotation={rotation} onProfileClick={onProfileClick} />;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DISCOVERY WIDGETS
// ─────────────────────────────────────────────────────────────────────────────

function SpinTheShelfWidget({ freshPicks, picksLoading, onAddPick }) {
  const [spinAngle, setSpinAngle] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [spinResult, setSpinResult] = useState(null);

  function handleSpin() {
    if (spinning || freshPicks.length === 0) return;
    setSpinning(true);
    setSpinResult(null);
    setSpinAngle(a => a + 1440 + Math.random() * 360);
    setTimeout(() => {
      setSpinning(false);
      setSpinResult(freshPicks[Math.floor(Math.random() * freshPicks.length)]);
    }, 2400);
  }

  const dots = Array.from({ length: 12 }, (_, i) => ({
    hue: [30, 60, 120, 200, 280, 340, 15, 90, 160, 240, 300, 45][i],
    angle: i * 30,
  }));

  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', border: '1px solid var(--brand-sand)' }}>
      <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 800, letterSpacing: '0.08em', color: '#166534', background: 'var(--feedback-success-bg)', padding: '3px 9px', borderRadius: 'var(--radius-10)', textTransform: 'uppercase' }}>
        Reader-Powered
      </span>
      <h3 style={{ fontFamily: 'Georgia, serif', fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--neutral-800)', margin: '10px 0 4px' }}>
        Feeling Lucky?
      </h3>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 16px', lineHeight: 1.5 }}>
        Every book here is a reader's recent 5-star obsession. Give it a spin!
      </p>

      {/* Wheel */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-14)' }}>
        <div style={{
          width: 88, height: 88, position: 'relative',
          transform: `rotate(${spinAngle}deg)`,
          transition: spinning ? 'transform 2.4s cubic-bezier(0.17,0.67,0.12,0.99)' : 'none',
        }}>
          {dots.map((dot, i) => {
            const rad = (dot.angle * Math.PI) / 180;
            const x = 44 + Math.cos(rad) * 34;
            const y = 44 + Math.sin(rad) * 34;
            return (
              <div key={i} style={{
                position: 'absolute', width: 11, height: 11, borderRadius: 'var(--radius-circle)',
                background: `hsl(${dot.hue}, 65%, 62%)`,
                left: x - 5.5, top: y - 5.5,
              }} />
            );
          })}
          <div style={{
            position: 'absolute', inset: '26%', borderRadius: 'var(--radius-circle)',
            background: 'var(--neutral-100)', border: '2px solid var(--brand-sand)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xl)',
          }}>
            🎰
          </div>
        </div>
      </div>

      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', textAlign: 'center', margin: '0 0 14px' }}>
        {freshPicks.length > 0
          ? `${freshPicks.length} books in the wheel from readers like you`
          : 'Be the first to add a 5-star read!'}
      </p>

      {spinResult && (
        <div style={{ background: '#fef9ec', borderRadius: 'var(--radius-10)', padding: 'var(--space-10)', marginBottom: 'var(--space-md)', display: 'flex', gap: 'var(--space-10)', border: '1px solid rgba(210,168,72,0.3)', animation: 'fadeIn 0.4s ease' }}>
          <div style={{ width: 36, height: 52, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(135deg,var(--feedback-warning-border),#f59e0b)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {spinResult.cover
              ? <img src={spinResult.cover} alt={spinResult.book} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
              : <span style={{ fontSize: 'var(--font-size-lg)' }}>📚</span>}
          </div>
          <div>
            <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px' }}>{spinResult.book}</p>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 2px' }}>{spinResult.author}</p>
            <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>@{spinResult.user}</p>
          </div>
        </div>
      )}

      <button
        onClick={handleSpin}
        disabled={spinning || freshPicks.length === 0}
        style={{
          width: '100%', padding: 11, marginBottom: 'var(--space-sm)',
          background: spinning || freshPicks.length === 0 ? 'var(--brand-sand)' : 'var(--neutral-800)',
          color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)',
          fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: spinning ? 'wait' : 'pointer',
          transition: 'background 0.2s',
        }}
      >
        {spinning ? '🎰 Spinning...' : '🎰 Spin the Wheel!'}
      </button>
      <button
        onClick={onAddPick}
        style={{ width: '100%', padding: 'var(--space-10)', background: 'transparent', color: 'var(--feedback-warning)', border: '1.5px dashed var(--feedback-warning-border)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer' }}
        onMouseEnter={e => { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.borderStyle = 'solid'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderStyle = 'dashed'; }}
      >
        + Add My 5-Star Read
      </button>
    </div>
  );
}

function FreshPicksWidget({ freshPicks, picksLoading, onAddPick }) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', padding: '14px 16px', border: '1px solid var(--brand-sand)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-10)' }}>
          <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>⭐ Fresh 5-Star Picks</h3>
          <button onClick={onAddPick}
            style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--space-none)' }}>
            + Add mine
          </button>
        </div>

        {picksLoading ? (
          <div style={{ display: 'flex', gap: 'var(--space-10)', overflowX: 'auto', paddingBottom: 'var(--space-xs)', scrollbarWidth: 'none' }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{ flexShrink: 0, width: 58, textAlign: 'center' }}>
                <div style={{ width: 58, height: 84, background: 'var(--neutral-100)', borderRadius: 6, marginBottom: 'var(--space-xs)' }} />
                <div style={{ height: 7, background: 'var(--neutral-100)', borderRadius: 'var(--radius-sm)', marginBottom: 'var(--space-xxs)' }} />
                <div style={{ height: 7, background: 'var(--neutral-100)', borderRadius: 'var(--radius-sm)', width: '70%', margin: '0 auto' }} />
              </div>
            ))}
          </div>
        ) : freshPicks.length > 0 ? (
          <div style={{ display: 'flex', gap: 'var(--space-10)', overflowX: 'auto', paddingBottom: 'var(--space-6)', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
            {freshPicks.map((pick, i) => (
              <div key={i} style={{ flexShrink: 0, width: 58, textAlign: 'center' }}>
                <div style={{ position: 'relative', marginBottom: 5 }}>
                  <div style={{ width: 58, height: 84, borderRadius: 6, overflow: 'hidden', background: 'linear-gradient(135deg, var(--feedback-warning-border), #f59e0b)', boxShadow: '1px 2px 6px rgba(0,0,0,0.12)', position: 'relative' }}>
                    {pick.cover
                      ? <img src={pick.cover} alt={pick.book} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xxl)' }}>📚</div>}
                    {/* Spine shadow */}
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: 'rgba(0,0,0,0.15)' }} />
                  </div>
                  {/* Star badge */}
                  <div style={{ position: 'absolute', top: -4, right: -4, background: '#fbbf24', borderRadius: 'var(--radius-circle)', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-micro)', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}>
                    ⭐
                  </div>
                </div>
                <p style={{ fontSize: 'var(--font-size-micro)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 1px', lineHeight: 1.2,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {pick.book}
                </p>
                <p style={{ fontSize: 7, color: 'var(--neutral-400)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  @{pick.user}
                </p>
              </div>
            ))}
            {/* Add pick card at the end */}
            <div
              onClick={onAddPick}
              style={{ flexShrink: 0, width: 58, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <div style={{ width: 58, height: 84, borderRadius: 6, border: '1.5px dashed var(--feedback-warning-border)', background: '#fffbeb', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, marginBottom: 5 }}>
                <span style={{ fontSize: 'var(--font-size-lg)' }}>+</span>
                <span style={{ fontSize: 7, color: 'var(--feedback-warning)', fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>Add yours</span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '0 0 8px' }}>No 5-star picks yet</p>
            <button onClick={onAddPick} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning)', background: 'none', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
              + Be the first
            </button>
          </div>
        )}
      </div>
    );
  }

  // Desktop — unchanged
  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', border: '1px solid var(--brand-sand)' }}>
      <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 14px' }}>⭐ Fresh 5-Star Picks</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
        {picksLoading ? (
          [1, 2, 3].map(i => <div key={i} style={{ height: 42, background: 'var(--neutral-100)', borderRadius: 'var(--radius-md)' }} />)
        ) : freshPicks.length > 0 ? (
          freshPicks.slice(0, 3).map((pick, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}>
              <div style={{ width: 32, height: 46, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(135deg,var(--feedback-warning-border),#f59e0b)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {pick.cover
                  ? <img src={pick.cover} alt={pick.book} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                  : <span style={{ fontSize: 'var(--font-size-md)' }}>📚</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pick.book}</p>
                <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>@{pick.user} · {pick.time}</p>
              </div>
              <span style={{ fontSize: 'var(--font-size-base)' }}>⭐</span>
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '0 0 8px' }}>No 5-star picks yet</p>
            <button onClick={onAddPick} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-warning)', background: 'none', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
              + Be the first
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TrendingTropesWidget({ tropes }) {
  const fallback = [['Slow Burn', '+24%'], ['Found Family', '+18%'], ['Dark Academia', '+11%']];
  const items = tropes.length > 0
    ? tropes.slice(0, 3).map(t => [t.name, t.growth || '+—'])
    : fallback;

  return (
    <div style={{ background: 'var(--neutral-800)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-14)' }}>
        <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--brand-paper)', margin: 'var(--space-none)' }}>📈 Trending Tropes</h3>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>This week</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
        {items.map(([name, growth], i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--brand-paper)', margin: 'var(--space-none)' }}>#{i + 1} {name}</p>
            </div>
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#4ade80' }}>{growth}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveActivityWidget({ activity }) {
  return (
    <div style={{ background: 'var(--feedback-success-bg)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', border: '1px solid #bbf7d0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-14)' }}>
        <div style={{ width: 8, height: 8, borderRadius: 'var(--radius-circle)', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.25)', flexShrink: 0 }} />
        <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Live Activity</h3>
        <span style={{ fontSize: 'var(--font-size-xxs)', color: '#86efac', marginLeft: 'auto' }}>Updates live</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {activity.slice(0, 5).map((act, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <div style={{
              width: 26, height: 26, borderRadius: 'var(--radius-circle)', flexShrink: 0,
              background: act.avatar_url ? `url(${act.avatar_url}) center/cover` : '#dcfce7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: '#166534',
            }}>
              {!act.avatar_url && act.user?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-800)', margin: 'var(--space-none)', lineHeight: 1.4 }}>
                <strong>@{act.user}</strong>{' '}
                <span style={{ color: 'var(--neutral-500)' }}>{act.action}</span>
                {act.book && !act.isCsvUpload && <strong> {act.book}</strong>}
              </p>
              <p style={{ fontSize: 'var(--font-size-tiny)', color: '#86efac', margin: '1px 0 0' }}>{act.time}</p>
            </div>
          </div>
        ))}
        {activity.length === 0 && (
          <p style={{ fontSize: 'var(--font-size-sm)', color: '#86efac', textAlign: 'center', padding: '8px 0' }}>No recent activity</p>
        )}
      </div>
    </div>
  );
}

function TasteMatchesWidget({ matches }) {
  return (
    <div style={{ background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', border: '1px solid #f9a8d4' }}>
      <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>💕 Your Taste Matches</h3>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 14px' }}>Readers with similar taste to you</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {matches.length > 0 ? matches.slice(0, 4).map((match, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <div style={{
              width: 30, height: 30, borderRadius: 'var(--radius-circle)', flexShrink: 0,
              background: match.avatar ? `url(${match.avatar}) center/cover` : '#f9a8d4',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#9d174d',
            }}>
              {!match.avatar && match.user?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)' }}>@{match.user}</span>
                <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: '#166534', background: 'var(--feedback-success-bg)', padding: '1px 5px', borderRadius: 'var(--radius-sm)' }}>{match.match}</span>
              </div>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{match.tropes}</p>
            </div>
            <button style={{ fontSize: 'var(--font-size-xl)', background: 'none', border: 'none', cursor: 'pointer', color: '#ec4899', padding: 'var(--space-none)' }}>+</button>
          </div>
        )) : (
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', textAlign: 'center', padding: '10px 0' }}>
            Set your preferences to find taste matches
          </p>
        )}
      </div>
      <button style={{ width: '100%', marginTop: 'var(--space-md)', padding: 'var(--space-sm)', background: 'transparent', color: '#9d174d', border: 'none', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer' }}>
        Find More Matches
      </button>
    </div>
  );
}

function BookClubsWidget({ clubs }) {
  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', border: '1px solid var(--brand-sand)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
        <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>📖 Book Clubs</h3>
        <button style={{ fontSize: 'var(--font-size-sm)', color: 'var(--brand-forest)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>+ Create</button>
      </div>
      {clubs.length > 0 ? (
        clubs.slice(0, 3).map(club => (
          <div key={club.id} style={{ padding: 'var(--space-10)', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}>
            <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: club.cover_color || 'var(--feedback-warning-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)', flexShrink: 0 }}>
              {club.emoji || '📚'}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>{club.name}</p>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{club.members_count} members</p>
            </div>
            <button style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--brand-forest)', background: 'none', border: '1px solid var(--neutral-300)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
              Join
            </button>
          </div>
        ))
      ) : (
        <div>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '0 0 10px' }}>No clubs yet, be the first!</p>
          <button style={{ width: '100%', padding: 'var(--space-10)', background: 'transparent', color: 'var(--brand-forest)', border: '1px dashed var(--neutral-300)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer' }}>
            + Create a Club
          </button>
        </div>
      )}
    </div>
  );
}

function HotTopicsWidget() {
  const tags = ['#SlowBurn', '#Romantasy', '#DarkAcademia', '#FoundFamily', '#BookTok', '#HotTake', '#Cozy', '#Spicy'];
  return (
    <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', border: '1px solid var(--brand-sand)' }}>
      <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 12px' }}>💬 Hot Topics</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
        {tags.map((tag, i) => (
          <span key={tag} style={{
            padding: '6px 12px',
            background: i < 3 ? 'var(--feedback-warning-bg)' : 'var(--brand-paper)',
            color: i < 3 ? 'var(--feedback-warning-ink)' : 'var(--neutral-600)',
            borderRadius: 'var(--radius-xl)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${i < 3 ? '#fde68a' : '#e8e4dc'}`,
          }}>
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

function CommunityChallengeWidget() {
  return (
    <div style={{ background: 'linear-gradient(135deg, var(--feedback-warning) 0%, var(--brand-forest) 100%)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', color: 'var(--brand-white)' }}>
      <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, margin: '0 0 8px' }}>🏆 Community Challenge</h3>
      <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 4px' }}>April Reading Challenge</p>
      <p style={{ fontSize: 'var(--font-size-sm)', opacity: 0.85, margin: '0 0 14px', lineHeight: 1.5 }}>Read 3 books with the 'Found Family' trope</p>
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-xs)', opacity: 0.8 }}>
          <span>1,247 joined</span><span>34%</span>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.2)', height: 7, borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
          <div style={{ background: '#fbbf24', height: '100%', width: '34%' }} />
        </div>
      </div>
      <button style={{ width: '100%', padding: 'var(--space-10)', background: 'rgba(255,255,255,0.2)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer' }}>
        Join Challenge
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BOOK PICKER — Reanlo DB + Google Books + Open Library
// ─────────────────────────────────────────────────────────────────────────────
function BookPickerModal({ onClose, onSelect }) {
  const [step, setStep]           = useState('search'); // 'search' | 'submit'
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef               = useRef(null);
  const abortRef                  = useRef(null); // tracks the current search controller

  // Submission form state
  const [form, setForm]           = useState({ title: '', author_name: '', blurb: '', isbn: '', publication_year: '' });
  // Real genres/subgenres tables (PR3, Control A surface #2) in place of
  // the old flat 15-name GENRES list -- see the deleted-array comment
  // below. submission_tags has no FK to subgenres (it's a pending-review
  // queue of plain tag_value strings, not a live book_tags row), so both
  // picks still land as flat strings at submit time; only the picking UI
  // and its vocabulary change.
  const { genres: pickerGenres, subgenresById: pickerSubgenresById,
    canonSubgenresForGenre: pickerCanonSubgenresForGenre, matchIndex: pickerMatchIndex,
    upsertPendingSubgenre: pickerUpsertPendingSubgenre,
    loading: pickerGenreLoading, error: pickerGenreError } = useGenreTaxonomy();
  const [selectedGenreIds, setSelectedGenreIds] = useState([]);
  const [selectedSubgenreIds, setSelectedSubgenreIds] = useState([]);
  const [subgenreNamesById, setSubgenreNamesById] = useState({});
  const [selTropes, setSelTropes] = useState([]);
  const [selMoods, setSelMoods]   = useState([]);
  const [selWarnings, setSelWarnings] = useState([]);
  const [availTropes, setAvailTropes] = useState([]);
  const [submitting, setSubmitting]   = useState(false);
  const [submitDone, setSubmitDone]   = useState(false);

  useEffect(() => {
    supabase.from('tropes').select('canonical_name').eq('category', 'trope').order('canonical_name')
      .then(({ data }) => setAvailTropes((data || []).map(t => t.canonical_name)));
  }, []);

  async function doSearch(q) {
    if (!q.trim()) { setResults([]); setSearching(false); return; }

    // Cancel any in-flight search
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    setSearching(true);
    const all = [];

    // 1. Reanlo DB (supabase doesn't support AbortSignal, but we guard on signal.aborted before using results)
    const { data: local } = await supabase
      .from('books').select('id, title, author_name, cover_image_url, publication_date, publication_year')
      .or(`title.ilike.%${q}%,author_name.ilike.%${q}%`).limit(4);

    if (signal.aborted) return;
    (local || []).forEach(b => all.push({ ...b, source: 'reanlo' }));

    // Show Reanlo's own books straight away. The outside sources below are
    // additions, not prerequisites, and when one of them is down waiting for
    // it is what turns a working search into an empty one.
    setResults(all.slice(0, 8));

    // 2. Outside books, through the edge function. It asks Google Books and
    // Open Library together and returns the union, so this is one call rather
    // than two tried in order — and the Google key never reaches the browser.
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetchWithTimeout(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/book-search`,
        {
          method: 'POST',
          headers: bookSearchHeaders(session, SUPABASE_PUBLISHABLE_KEY),
          body: JSON.stringify({ query: q }),
          signal,
        },
        SEARCH_BOX_TIMEOUT_MS
      );
      if (res.ok) {
        const data = await res.json();
        if (signal.aborted) return;
        (data.results || []).forEach(b => {
          if (!b.title) return;
          if (all.some(r => r.title?.toLowerCase() === b.title?.toLowerCase())) return;
          all.push({
            id: null,
            google_id: b.google_id || null,
            ol_key: b.work_key || null,
            title: b.title,
            author_name: b.author_name || 'Unknown',
            cover_image_url: b.cover_image_url || null,
            publication_year: b.publication_year || null,
            source: b.source || 'openlibrary',
          });
        });
      }
    } catch {
      // A deadline aborts too, so the error alone cannot say whether this
      // search was superseded. Only the caller's own signal can.
      if (signal.aborted) return;
    }

    if (signal.aborted) return;
    setResults(all.slice(0, 8));
    setSearching(false);
  }

  function handleChange(e) {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 380);
  }

  function handleSelect(e, book) {
    e.stopPropagation();
    onSelect(book);
  }

  async function handleSubmitRequest(e) {
    e.stopPropagation();
    if (!form.title.trim() || !form.author_name.trim()) return;
    setSubmitting(true);
    try {
      const user = await getCurrentUser();
      if (!user) return;
      const { data: sub, error } = await supabase.from('book_submissions').insert({
        submitted_by: user.id,
        title: form.title, author_name: form.author_name,
        blurb: form.blurb, isbn: form.isbn,
        publication_year: form.publication_year ? parseInt(form.publication_year) : null,
        status: 'pending',
      }).select().single();
      if (error) throw error;
      const genreNames = selectedGenreIds
        .map(id => pickerGenres.find(g => g.id === id)?.name)
        .filter(Boolean);
      const subgenreNames = selectedSubgenreIds
        .map(id => pickerSubgenresById.get(id)?.name || subgenreNamesById[id]?.name)
        .filter(Boolean);
      const tags = [
        ...genreNames.map(g    => ({ submission_id: sub.id, tag_type: 'genre',           tag_value: g  })),
        ...subgenreNames.map(s => ({ submission_id: sub.id, tag_type: 'subgenre',        tag_value: s  })),
        ...selTropes.map(t  => ({ submission_id: sub.id, tag_type: 'trope',           tag_value: t  })),
        ...selMoods.map(m   => ({ submission_id: sub.id, tag_type: 'mood',            tag_value: m  })),
        ...selWarnings.map(w=> ({ submission_id: sub.id, tag_type: 'content_warning', tag_value: w  })),
      ];
      if (tags.length > 0) await supabase.from('submission_tags').insert(tags);
      setSubmitDone(true);
    } catch (err) {
      console.error('Book submission error:', err.message);
    }
    setSubmitting(false);
  }

  function toggle(arr, setArr, val) {
    setArr(arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val]);
  }

  // Same amber look the old flat GENRES chips used, handed to
  // GenreSubgenrePicker as its injected Chip.
  function BookRequestGenreChip({ on, onClick, children }) {
    return (
      <button onClick={onClick} style={{ padding: '5px 11px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer', transition: 'all 0.12s',
        border: on ? '2px solid #d97706' : '2px solid var(--brand-sand)',
        background: on ? '#fef3c7' : 'var(--brand-white)',
        color: on ? '#92400e' : 'var(--neutral-600)' }}>
        {on && '\u2713 '}{children}
      </button>
    );
  }

  const BADGE = {
    reanlo:      { bg: '#292524', color: 'var(--brand-white)', label: 'REANLO' },
    google:      { bg: '#4285f4', color: 'var(--brand-white)', label: 'GOOGLE' },
    openlibrary: { bg: '#e63c2f', color: 'var(--brand-white)', label: 'OPEN LIB' },
  };
  // The flat 15-name GENRES list is superseded by pickerGenres/
  // GenreSubgenrePicker above (PR3, docs/genre-subgenre-build-spec.md,
  // Control A surface #2) -- unlike SUGGESTED_GENRES elsewhere in this
  // file, this one was actually rendered, so it is a replacement, not a
  // deletion of dead code.
  const MOODS    = ['Dark','Light & Fluffy','Angsty','Humorous','Emotional','Fast-Paced','Character-Driven','Plot-Driven','Atmospheric','Witty Banter','Cozy','Gritty'];
  const WARNINGS = ['Violence','Sexual Assault','Abuse','Self-Harm','Suicide','Death of Parent','Death of Child','Animal Death','Addiction','Mental Illness'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 520, width: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid var(--neutral-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontFamily: 'Georgia, serif', fontSize: 17, margin: 'var(--space-none)' }}>
            {step === 'search' ? '📖 Attach a Book' : '📬 Request a Book'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xxl)', cursor: 'pointer', color: 'var(--neutral-400)', lineHeight: 1 }}>✕</button>
        </div>

        {step === 'search' ? (
          <>
            {/* Search input */}
            <div style={{ padding: '14px 24px 0' }}>
              <input value={query} onChange={handleChange} autoFocus
                placeholder="Search by title or author..."
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', outline: 'none', boxSizing: 'border-box' }} />
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '5px 0 0' }}>Searches Reanlo, Google Books, and Open Library</p>
            </div>

            {/* Results list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 24px', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', scrollbarWidth: 'none' }}>
              {searching && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', textAlign: 'center', padding: '16px 0' }}>Searching…</p>}
              {results.map((book, i) => {
                const badge = BADGE[book.source] || BADGE.google;
                return (
                  <button key={book.id || book.google_id || book.ol_key || i}
                    onClick={e => handleSelect(e, book)}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '10px 12px', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <div style={{ width: 32, height: 46, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--neutral-100)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {book.cover_image_url
                        ? <img src={book.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                        : <span style={{ fontSize: 'var(--font-size-lg)' }}>📚</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.title}</p>
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {book.author_name}{publicationYear(book) ? ` · ${publicationYear(book)}` : ''}</p>
                    </div>
                    <span style={{ fontSize: 'var(--font-size-micro)', fontWeight: 700, padding: '2px 6px', borderRadius: 'var(--radius-sm)', background: badge.bg, color: badge.color, flexShrink: 0 }}>{badge.label}</span>
                  </button>
                );
              })}
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', textAlign: 'center', padding: '20px 0' }}>No books found</p>
              )}
            </div>

            {/* Can't find it? */}
            <div style={{ padding: '12px 24px 20px', borderTop: '1px solid var(--neutral-100)', background: 'var(--feedback-warning-bg)', textAlign: 'center' }}>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-warning-ink)', margin: '0 0 8px' }}>Can't find the book you're looking for?</p>
              <button onClick={() => setStep('submit')}
                style={{ padding: '8px 18px', background: 'var(--feedback-warning)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer' }}>
                + Request to add it to Reanlo
              </button>
            </div>
          </>
        ) : submitDone ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-h3)', textAlign: 'center' }}>
            <span style={{ fontSize: 'var(--font-size-hero)', marginBottom: 'var(--space-md)' }}>🎉</span>
            <p style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Request submitted!</p>
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 20px' }}>We'll review it within 24–48 hours and add it to Reanlo.</p>
            <button onClick={onClose} style={{ padding: '10px 24px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          /* Submission form */
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px 20px', scrollbarWidth: 'none' }}>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 16px', lineHeight: 1.5 }}>Help grow our database! We'll review your request within 24–48 hours.</p>

            {[{ key: 'title', label: 'BOOK TITLE *', placeholder: 'e.g. A Court of Thorns and Roses' },
              { key: 'author_name', label: 'AUTHOR *', placeholder: 'e.g. Sarah J. Maas' }].map(f => (
              <div key={f.key} style={{ marginBottom: 'var(--space-md)' }}>
                <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-600)', margin: '0 0 4px', letterSpacing: 0.5 }}>{f.label}</p>
                <input value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-10)', marginBottom: 'var(--space-md)' }}>
              {[{ key: 'isbn', label: 'ISBN', placeholder: 'ISBN-13' },
                { key: 'publication_year', label: 'YEAR', placeholder: '2024', type: 'number' }].map(f => (
                <div key={f.key}>
                  <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-600)', margin: '0 0 4px', letterSpacing: 0.5 }}>{f.label}</p>
                  <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 'var(--space-14)' }}>
              <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-600)', margin: '0 0 4px', letterSpacing: 0.5 }}>BLURB (optional)</p>
              <textarea value={form.blurb} onChange={e => setForm({ ...form, blurb: e.target.value })} placeholder="Brief description…" rows={2}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            </div>

            <div style={{ marginBottom: 'var(--space-14)' }}>
              <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-600)', margin: '0 0 6px', letterSpacing: 0.5 }}>GENRES * (pick at least 1)</p>
              <GenreSubgenrePicker
                Chip={BookRequestGenreChip}
                genres={pickerGenres}
                canonSubgenresForGenre={pickerCanonSubgenresForGenre}
                matchIndex={pickerMatchIndex}
                upsertPendingSubgenre={pickerUpsertPendingSubgenre}
                selectedGenreIds={selectedGenreIds}
                setSelectedGenreIds={setSelectedGenreIds}
                selectedSubgenreIds={selectedSubgenreIds}
                setSelectedSubgenreIds={setSelectedSubgenreIds}
                subgenreNamesById={subgenreNamesById}
                setSubgenreNamesById={setSubgenreNamesById}
                loading={pickerGenreLoading}
                error={pickerGenreError}
              />
            </div>

            {[{ label: 'TROPES * (3 combined with mood, minimum)', items: availTropes.length > 0 ? availTropes : [], sel: selTropes, setSel: setSelTropes, activeColor: '#B85C38', activeBg: '#fef3c7', activeText: '#92400e' },
              { label: 'MOOD (optional)', items: MOODS, sel: selMoods, setSel: setSelMoods, activeColor: '#6b4c3b', activeBg: '#fef3c7', activeText: '#92400e' },
              { label: 'CONTENT WARNINGS (optional)', items: WARNINGS, sel: selWarnings, setSel: setSelWarnings, activeColor: '#b91c1c', activeBg: '#fef2f2', activeText: '#b91c1c' },
            ].map(({ label, items, sel, setSel, activeColor, activeBg, activeText }) => (
              <div key={label} style={{ marginBottom: 'var(--space-14)' }}>
                <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-600)', margin: '0 0 6px', letterSpacing: 0.5 }}>{label}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {items.map(item => (
                    <button key={item} onClick={() => toggle(sel, setSel, item)}
                      style={{ padding: '5px 11px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer', transition: 'all 0.12s',
                        border: sel.includes(item) ? `2px solid ${activeColor}` : '2px solid var(--brand-sand)',
                        background: sel.includes(item) ? activeBg : 'var(--brand-white)',
                        color: sel.includes(item) ? activeText : 'var(--neutral-600)' }}>
                      {sel.includes(item) && '✓ '}{item}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 'var(--space-10)', marginTop: 'var(--space-sm)' }}>
              <button onClick={() => setStep('search')} style={{ flex: 1, padding: '11px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer', color: 'var(--neutral-500)' }}>← Back</button>
              <button onClick={handleSubmitRequest}
                disabled={submitting || !form.title.trim() || !form.author_name.trim() || selectedGenreIds.length === 0 || (selTropes.length + selMoods.length) < 3}
                style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                  background: (!submitting && form.title.trim() && form.author_name.trim() && selectedGenreIds.length > 0 && (selTropes.length + selMoods.length) >= 3) ? '#10b981' : 'var(--brand-sand)',
                  color: 'var(--brand-white)' }}>
                {submitting ? 'Submitting…' : '✓ Submit Request'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSE MODAL
// ─────────────────────────────────────────────────────────────────────────────

function ComposeModal({ currentUser, onClose, onPost }) {
  const isMobile = useIsMobile();
  const [type, setType]             = useState('thought');
  const [text, setText]             = useState('');
  const [spoiler, setSpoiler]       = useState(false);
  const [disappears, setDisappears] = useState('never');
  const [selectedBook, setSelectedBook] = useState(null);
  const [showBookPicker, setShowBookPicker] = useState(false);
  const [posting, setPosting]       = useState(false);
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [imageUrl, setImageUrl]     = useState(null);
  const [uploading, setUploading]   = useState(false);
  const fileRef                     = useRef(null);

  // ── Tag step for external books ─────────────────────────────────────
  const [bookTagStep, setBookTagStep]   = useState(false);
  // { tropes, tone, spice } while the step is open -- .genre is filled in by
  // handleSubmit, from selectedGenreIds/selectedSubgenreIds below, not
  // edited directly here (see handleBookSelect).
  const [bookTags, setBookTags]         = useState(null);
  const [customTagInput, setCustomTagInput] = useState('');
  const [tagWarning,     setTagWarning]     = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Genre/subgenre for a new external book, same taxonomy and picker every
  // other reader surface uses (docs/genre-subgenre-build-spec.md, PR 3 /
  // PR 5). A Reanlo platform book (book.id set) skips bookTagStep entirely
  // in handleBookSelect below, so this state is only ever exercised for a
  // brand-new book with no existing genre from anyone -- there is no
  // "already has one" case to check for at this entry point.
  const { genres: composerGenres, subgenresById: composerSubgenresById,
    canonSubgenresForGenre: composerCanonSubgenresForGenre, matchIndex: composerMatchIndex,
    upsertPendingSubgenre: composerUpsertPendingSubgenre,
    loading: composerGenreLoading, error: composerGenreError } = useGenreTaxonomy();
  const [selectedGenreIds, setSelectedGenreIds] = useState([]);
  const [selectedSubgenreIds, setSelectedSubgenreIds] = useState([]);
  const [subgenreNamesById, setSubgenreNamesById] = useState({});

  const SUGGESTED_TROPES = [
    'Enemies to Lovers', 'Slow Burn', 'Forced Proximity', 'Second Chance',
    'Fake Dating', 'Friends to Lovers', 'Forbidden Love', 'Grumpy x Sunshine',
    'Found Family', 'Redemption Arc', 'Morally Grey MMC', 'Dual POV',
  ];
  // SUGGESTED_GENRES was deleted here (PR3, docs/genre-subgenre-build-spec.md
  // Part 3B): declared, never rendered anywhere in this file or the repo --
  // confirmed dead code, not a ninth picker as an earlier draft of that spec
  // mistakenly counted it.
  const SPICE_LABELS = ['No Spice', 'Mild', 'Moderate', 'Spicy', 'Scorching'];

  // When a book is selected — if external, show manual tag step
  function handleBookSelect(book) {
    setSelectedBook(book);
    setShowBookPicker(false);
    setSelectedGenreIds([]);
    setSelectedSubgenreIds([]);
    setSubgenreNamesById({});

    // Reanlo books already have tags — no tag step needed
    if (book.id) {
      setBookTags(null);
      setBookTagStep(false);
      return;
    }

    // External book — show tag step with blank defaults. Genre is picked
    // from the canon list below (GenreSubgenrePicker), never defaulted from
    // book.genres -- that's Open Library/Goodreads free text, not a name off
    // the 20-genre list, and defaulting it in silently is exactly the shape
    // of book PR 5's review flagged: a genre nobody actually picked.
    setBookTagStep(true);
    setBookTags({
      tropes: [],
      tone:   [],
      spice:  'Moderate',
    });
  }

  function toggleTrope(trope) {
    setBookTags(prev => {
      const list = prev.tropes || [];
      return {
        ...prev,
        tropes: list.includes(trope) ? list.filter(t => t !== trope) : [...list, trope],
      };
    });
  }

  function addCustomTag() {
    const val = customTagInput.trim();
    if (!val) return;
    // Refuse rather than truncate, and leave their text alone so they can edit
    // it down. See tagNameError in tropeUtils for why this is not a maxLength.
    const problem = tagNameError(val);
    if (problem) { setTagWarning(problem); return; }
    setBookTags(prev => ({ ...prev, tropes: [...(prev.tropes || []), val] }));
    setCustomTagInput('');
    setTagWarning('');
    setShowCustomInput(false);
  }

  // Same shape as every other GenreSubgenrePicker caller in this repo --
  // CsvGenreChip, FiveStarGenreChip, BookRequestGenreChip above -- each
  // surface gets its own small Chip rather than sharing one.
  function ComposerGenreChip({ on, onClick, children }) {
    return (
      <button onClick={onClick} style={{ padding: '5px 11px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer', transition: 'all 0.12s',
        border: on ? '2px solid #d97706' : '2px solid var(--brand-sand)',
        background: on ? '#fef3c7' : 'var(--brand-white)',
        color: on ? '#92400e' : 'var(--neutral-600)' }}>
        {on && '\u2713 '}{children}
      </button>
    );
  }

  const tabs = [
    { id: 'thought', label: '💭 Thought' },
    { id: 'reading', label: '📖 Reading' },
    { id: 'poll',    label: '📊 Poll' },
    { id: 'image',   label: '🖼️ Image' },
  ];

  const placeholders = {
    thought: "What's on your reading mind?",
    reading: 'What are you reading and how does it make you feel?',
    poll:    "Ask the community something…",
    image:   'Write a caption…',
  };

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const user = await getCurrentUser();
      const ext  = file.name.split('.').pop();
      const path = `community/${user.id}/${Date.now()}.${ext}`;
      await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
      setImageUrl(publicUrl);
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit() {
    if (type === 'image' && !imageUrl && !text.trim()) return;
    if (type === 'poll' && !pollOptions.some(o => o.trim())) return;
    if (type !== 'poll' && type !== 'image' && !text.trim() && !selectedBook) return;
    setPosting(true);
    try {
      // Resolved here, not in handlePost -- composerGenres/subgenreNamesById
      // are this component's own picker state, same pattern
      // Add5StarReadPopup.jsx uses right before its own save. bookTags.genre
      // keeps carrying names, the shape resolveAllTagsToBookTags and the
      // book_submissions record in handlePost already expect; the raw ids
      // go up alongside it for writeReaderGenrePicks, which needs ids, not
      // names.
      const genreNames = selectedGenreIds
        .map(id => composerGenres.find(g => g.id === id)?.name)
        .filter(Boolean);
      const subgenreNames = selectedSubgenreIds
        .map(id => composerSubgenresById.get(id)?.name || subgenreNamesById[id]?.name)
        .filter(Boolean);
      const finalBookTags = bookTags ? { ...bookTags, genre: [...genreNames, ...subgenreNames] } : bookTags;
      await onPost({
        type, text, selectedBook, spoiler, disappears, pollOptions, imageUrl,
        bookTags: finalBookTags, selectedGenreIds, selectedSubgenreIds,
      });
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setPosting(false);
    }
  }

  // The composer's own missing PR 3 save gate (review on PR 5): an
  // external book (bookTagStep open) had nothing here requiring a genre or
  // a trope floor before it could post. genre-subgenre-build-spec.md:1797
  // requires 1+ genre, "conditionally" -- only when the book doesn't
  // already carry one from anyone -- plus 3 tropes-and-tags combined;
  // subgenre stays optional, never a wall. The condition on genre never
  // applies at this entry point: handleBookSelect above only opens this
  // step for a book with no id at all, so it can never already carry a
  // genre the way an existing Reanlo book might.
  const bookTagsReady = !bookTagStep || (
    selectedGenreIds.length > 0 &&
    ((bookTags?.tropes?.length || 0) + (bookTags?.tone?.length || 0)) >= 3
  );

  const canPost = (
    (type === 'thought' && text.trim()) ||
    (type === 'reading' && (text.trim() || selectedBook)) ||
    (type === 'poll' && pollOptions.some(o => o.trim())) ||
    (type === 'image' && (imageUrl || text.trim()))
  ) && bookTagsReady;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.52)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 20 }}
        onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={{
          background: '#fef9ec',
          borderRadius: isMobile ? '20px 20px 0 0' : 4,
          padding: isMobile ? '24px 20px 32px' : '36px 28px 24px',
          width: '100%', maxWidth: isMobile ? '100%' : 500, position: 'relative',
          boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
          border: '1px solid rgba(210,168,72,0.4)',
          animation: isMobile ? 'slideUpSheet 0.25s ease' : 'modalIn 0.2s ease',
          maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto',
        }}>
          {/* Tape */}
          <div style={{ position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)', width: 64, height: 22, background: 'rgba(253,233,170,0.85)', borderRadius: 3, border: '1px solid rgba(210,168,72,0.45)', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }} />
          <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', cursor: 'pointer', color: 'var(--neutral-400)', padding: 'var(--space-xs)', lineHeight: 1 }}>✕</button>

          {/* Type tabs */}
          <div style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 18, background: 'rgba(0,0,0,0.06)', borderRadius: 'var(--radius-10)', padding: 'var(--space-xs)' }}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setType(tab.id)} style={{
                flex: 1, padding: '8px 4px',
                background: type === tab.id ? 'var(--brand-white)' : 'transparent',
                color: 'var(--neutral-800)', border: 'none', borderRadius: 7,
                fontSize: 'var(--font-size-sm)', fontWeight: type === tab.id ? 700 : 500,
                cursor: 'pointer', transition: 'all 0.15s',
                boxShadow: type === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* User row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', marginBottom: 'var(--space-14)' }}>
            <div style={{
              width: 32, height: 32, borderRadius: 'var(--radius-circle)', flexShrink: 0, overflow: 'hidden',
              background: currentUser?.avatar_url ? `url(${currentUser.avatar_url}) center/cover` : 'var(--brand-forest)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-white)', fontWeight: 700, fontSize: 'var(--font-size-base)',
            }}>
              {!currentUser?.avatar_url && (currentUser?.username?.[0]?.toUpperCase() || 'U')}
            </div>
            <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)' }}>@{currentUser?.username || 'reader'}</span>
          </div>

          {/* Image upload */}
          {type === 'image' && (
            <div style={{ marginBottom: 'var(--space-md)' }}>
              {imageUrl ? (
                <div style={{ position: 'relative', borderRadius: 'var(--radius-10)', overflow: 'hidden', marginBottom: 'var(--space-10)' }}>
                  <img src={imageUrl} alt="preview" style={{ width: '100%', maxHeight: 260, objectFit: 'cover', display: 'block' }} />
                  <button onClick={() => setImageUrl(null)}
                    style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 'var(--radius-circle)', width: 28, height: 28, color: 'var(--brand-white)', fontSize: 'var(--font-size-md)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                </div>
              ) : (
                <div onClick={() => fileRef.current?.click()}
                  style={{ width: '100%', height: 140, border: '1.5px dashed rgba(210,168,72,0.6)', borderRadius: 'var(--radius-10)', background: 'rgba(255,255,255,0.5)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'wait' : 'pointer', gap: 'var(--space-sm)' }}>
                  {uploading
                    ? <><div style={{ width: 24, height: 24, border: '3px solid var(--feedback-warning-border)', borderTopColor: 'var(--feedback-warning)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.8s linear infinite' }} /><span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>Uploading…</span></>
                    : <><span style={{ fontSize: 'var(--font-size-h2)' }}>🖼️</span><span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', fontWeight: 500 }}>Click to upload a photo</span><span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>JPG, PNG, GIF: up to 5MB</span></>}
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
            </div>
          )}

          {/* Main text */}
          <textarea value={text} onChange={e => setText(e.target.value)}
            placeholder={placeholders[type]}
            rows={type === 'thought' ? 4 : type === 'image' ? 2 : 3}
            style={{
              width: '100%', padding: 'var(--space-md)', boxSizing: 'border-box',
              fontFamily: type === 'thought' ? 'Georgia, serif' : 'inherit',
              fontStyle: type === 'thought' ? 'italic' : 'normal',
              fontSize: 'var(--font-size-md)', color: 'var(--neutral-800)',
              border: '1px solid rgba(210,168,72,0.4)', borderRadius: 'var(--radius-md)',
              outline: 'none', resize: 'none',
              background: 'rgba(255,255,255,0.65)',
            }}
          />

          {/* Poll options */}
          {type === 'poll' && (
            <div style={{ marginTop: 'var(--space-10)', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              {pollOptions.map((opt, i) => (
                <div key={i} style={{ display: 'flex', gap: 'var(--space-6)' }}>
                  <input value={opt} onChange={e => setPollOptions(prev => prev.map((o, j) => j === i ? e.target.value : o))}
                    placeholder={`Option ${i + 1}`}
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid rgba(210,168,72,0.4)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', background: 'rgba(255,255,255,0.65)' }} />
                  {pollOptions.length > 2 && (
                    <button onClick={() => setPollOptions(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-lg)', color: 'var(--neutral-400)' }}>×</button>
                  )}
                </div>
              ))}
              {pollOptions.length < 4 && (
                <button onClick={() => setPollOptions(prev => [...prev, ''])} style={{ padding: '7px', background: 'transparent', color: 'var(--neutral-500)', border: '1px dashed rgba(210,168,72,0.5)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
                  + Add option
                </button>
              )}
            </div>
          )}

          {/* Book attach — reading tab */}
          {type === 'reading' && (
            <div style={{ marginTop: 'var(--space-10)' }}>
              {selectedBook ? (
                <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-10)', border: '1px solid var(--neutral-100)', overflow: 'hidden' }}>
                  {/* Book row */}
                  <div style={{ display: 'flex', gap: 'var(--space-10)', alignItems: 'center', padding: 'var(--space-10)' }}>
                    <div style={{ width: 36, height: 52, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: 'var(--feedback-warning-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {selectedBook.cover_image_url
                        ? <img src={selectedBook.cover_image_url} alt={selectedBook.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                        : <span style={{ fontSize: 'var(--font-size-xl)' }}>📚</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px' }}>{selectedBook.title}</p>
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{selectedBook.author_name}</p>
                      {!selectedBook.id && (
                        <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--feedback-warning)', background: 'var(--feedback-warning-bg)', padding: '1px 6px', borderRadius: 'var(--radius-sm)' }}>
                          NEW, will be submitted for review
                        </span>
                      )}
                    </div>
                    <button onClick={() => { setSelectedBook(null); setBookTags(null); setBookTagStep(false); }}
                      style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', cursor: 'pointer', color: 'var(--neutral-400)' }}>×</button>
                  </div>

                  {/* ── Manual tag step for external books ── */}
                  {bookTagStep && bookTags && (
                    <div style={{ borderTop: '1px solid var(--neutral-100)', padding: '12px 12px 14px', background: 'var(--brand-paper)' }}>
                      <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px' }}>
                        🏷️ Tag this book
                      </p>

                      {/* Genre -- required, one of the 20 canon genres.
                          Same GenreSubgenrePicker every other reader surface
                          uses; subgenre is asked for and never blocks
                          (docs/genre-subgenre-build-spec.md:1797). */}
                      <div style={{ marginBottom: 'var(--space-sm)' }}>
                        <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-600)', margin: '0 0 6px', letterSpacing: 0.5 }}>GENRE * (pick at least 1)</p>
                        <GenreSubgenrePicker
                          Chip={ComposerGenreChip}
                          genres={composerGenres}
                          canonSubgenresForGenre={composerCanonSubgenresForGenre}
                          matchIndex={composerMatchIndex}
                          upsertPendingSubgenre={composerUpsertPendingSubgenre}
                          selectedGenreIds={selectedGenreIds}
                          setSelectedGenreIds={setSelectedGenreIds}
                          selectedSubgenreIds={selectedSubgenreIds}
                          setSelectedSubgenreIds={setSelectedSubgenreIds}
                          subgenreNamesById={subgenreNamesById}
                          setSubgenreNamesById={setSubgenreNamesById}
                          loading={composerGenreLoading}
                          error={composerGenreError}
                        />
                      </div>

                      {/* Selected tropes */}
                      {bookTags.tropes?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 'var(--space-sm)' }}>
                          {bookTags.tropes.map(t => (
                            <span key={t} onClick={() => toggleTrope(t)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '3px 9px', borderRadius: 14, background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)', cursor: 'pointer' }}>
                              {t} <span style={{ fontSize: 'var(--font-size-tiny)' }}>×</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Suggested tropes not yet selected */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 'var(--space-sm)' }}>
                        {SUGGESTED_TROPES.filter(t => !bookTags.tropes?.includes(t)).slice(0, 8).map(t => (
                          <span key={t} onClick={() => toggleTrope(t)}
                            style={{ fontSize: 'var(--font-size-xs)', padding: '3px 9px', borderRadius: 14, background: 'var(--brand-white)', color: 'var(--neutral-500)', border: '1px solid var(--brand-sand)', cursor: 'pointer' }}>
                            {t}
                          </span>
                        ))}
                      </div>

                      {/* Spice */}
                      <div style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 'var(--space-sm)', flexWrap: 'wrap' }}>
                        {SPICE_LABELS.map(s => (
                          <button key={s} onClick={() => setBookTags(prev => ({ ...prev, spice: s }))}
                            style={{ fontSize: 'var(--font-size-xxs)', padding: '3px 9px', borderRadius: 'var(--radius-lg)', cursor: 'pointer', border: '1px solid', fontWeight: bookTags.spice === s ? 700 : 400, background: bookTags.spice === s ? 'var(--neutral-800)' : 'var(--brand-white)', color: bookTags.spice === s ? 'var(--brand-white)' : 'var(--neutral-500)', borderColor: bookTags.spice === s ? 'var(--neutral-800)' : 'var(--brand-sand)' }}>
                            {s}
                          </button>
                        ))}
                      </div>

                      {/* Custom trope */}
                      {showCustomInput ? (
                        <>
                        <div style={{ display: 'flex', gap: 'var(--space-6)', marginTop: 'var(--space-xs)' }}>
                          <input
                            autoFocus
                            value={customTagInput}
                            onChange={e => { setCustomTagInput(e.target.value); if (tagWarning) setTagWarning(''); }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') addCustomTag();
                              if (e.key === 'Escape') { setShowCustomInput(false); setCustomTagInput(''); setTagWarning(''); }
                            }}
                            placeholder="Type a trope..."
                            aria-invalid={tagWarning ? 'true' : undefined}
                            aria-describedby={tagWarning ? 'composer-tag-warning' : undefined}
                            style={{ flex: 1, fontSize: 'var(--font-size-xs)', padding: '4px 9px', borderRadius: 'var(--radius-md)', border: tagWarning ? '1px solid var(--feedback-warning-border)' : '1px solid var(--feedback-warning)', outline: 'none', fontFamily: 'inherit' }}
                          />
                          <button onClick={addCustomTag} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px', background: 'var(--feedback-warning)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 600 }}>Add</button>
                          <button onClick={() => { setShowCustomInput(false); setCustomTagInput(''); setTagWarning(''); }} style={{ fontSize: 'var(--font-size-base)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--neutral-400)' }}>×</button>
                        </div>
                        {tagWarning && (
                          <p id="composer-tag-warning" role="status" style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning-ink)', margin: '6px 0 0', lineHeight: 1.4 }}>
                            {tagWarning}
                          </p>
                        )}
                        </>
                      ) : (
                        <button onClick={() => setShowCustomInput(true)}
                          style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning)', background: 'none', border: '1px dashed var(--feedback-warning-border)', borderRadius: 'var(--radius-md)', padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}>
                          + Add custom trope
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => setShowBookPicker(true)}
                  style={{ width: '100%', padding: 'var(--space-10)', background: 'transparent', color: 'var(--neutral-500)', border: '1.5px dashed rgba(210,168,72,0.5)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', cursor: 'pointer' }}>
                  + Attach a book
                </button>
              )}
            </div>
          )}

          {/* Bottom controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginTop: 'var(--space-lg)', flexWrap: 'wrap' }}>
            {type !== 'image' && (
              <button onClick={() => setSpoiler(p => !p)}
                style={{ padding: '7px 12px', background: spoiler ? '#fef2f2' : 'transparent', color: spoiler ? 'var(--feedback-error)' : 'var(--neutral-500)', border: `1px solid ${spoiler ? '#fca5a5' : '#e8e4dc'}`, borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer' }}>
                🚨 Spoiler
              </button>
            )}
            <select value={disappears} onChange={e => setDisappears(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', background: 'var(--brand-white)', cursor: 'pointer' }}>
              <option value="never">⏱ Never</option>
              <option value="12h">⏳ 12h</option>
              <option value="24h">⏳ 24h</option>
            </select>
            <button onClick={handleSubmit} disabled={posting || !canPost}
              style={{
                marginLeft: 'auto', padding: '9px 24px',
                background: canPost && !posting ? '#9E4B2C' : 'var(--brand-sand)',
                color: canPost && !posting ? 'var(--brand-white)' : 'var(--neutral-400)',
                border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-md)', fontWeight: 700,
                cursor: canPost && !posting ? 'pointer' : 'not-allowed',
                transition: 'background 0.2s',
              }}>
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      </div>

      {showBookPicker && (
        <BookPickerModal
          onClose={() => setShowBookPicker(false)}
          onSelect={handleBookSelect}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SIGNUP PROMPT (10-second scroll trigger)
// ─────────────────────────────────────────────────────────────────────────────

function SignupPrompt({ onDismiss }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      zIndex: 900, background: 'var(--neutral-800)', borderRadius: 'var(--radius-xl)', padding: '18px 24px',
      boxShadow: '0 8px 40px rgba(0,0,0,0.28)', display: 'flex', alignItems: 'center', gap: 'var(--space-14)',
      maxWidth: 460, width: 'calc(100% - 40px)', animation: 'slideUp 0.3s ease',
    }}>
      <div style={{ fontSize: 30, flexShrink: 0 }}>📚</div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--brand-paper)', margin: '0 0 2px' }}>Join the conversation</p>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Create an account to post, like, and connect with readers.</p>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexShrink: 0 }}>
        <button onClick={onDismiss} style={{ padding: '8px 14px', background: 'transparent', color: 'var(--neutral-400)', border: '1px solid var(--neutral-700)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', cursor: 'pointer' }}>
          Later
        </button>
        <button style={{ padding: '8px 16px', background: 'var(--feedback-warning)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer' }}>
          Sign Up
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMMUNITY PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function Community() {
  const { addToast } = useToast();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('general');
  const [showCompose, setShowCompose] = useState(false);
  const [show5StarModal, setShow5StarModal] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const gate = useGuestGate('community');
  const [currentUser, setCurrentUser] = useState(null);
  const [freshPicks, setFreshPicks] = useState([]);
  const [picksLoading, setPicksLoading] = useState(true);
  const [profileEvents, setProfileEvents] = useState([]);
  const profileEventsFetchedRef = useRef(false);
  const [eventsPage, setEventsPage] = useState(1);
  const EVENTS_PER_PAGE = 12;

  const { posts: rawPosts, loading: postsLoading, refetch: refetchPosts } = usePosts(activeTab);
  const { activity } = useLiveActivity();
  const { tropes } = useTrendingTropes();
  const { clubs } = useBookClubs();
  const { matches } = useTasteMatches();
  const { stats, refetch: refetchStats } = useCommunityStats();

  // Normalize: usePosts returns user data nested in post.user_profiles,
  // but all card components read post.username / post.avatar_url directly.
  const posts = rawPosts.map(p => ({
    ...p,
    username:     p.user_profiles?.username    || p.username    || 'reader',
    avatar_url:   p.user_profiles?.avatar_url  || p.avatar_url  || null,
    display_name: p.user_profiles?.display_name|| p.display_name|| null,
    // Also flatten books join — ReadingUpdateCard reads post.books
    books: p.books || null,
  }));

  useEffect(() => { fetchCurrentUser(); }, []);
  useEffect(() => { fetchFreshPicks(); }, []);

  // Fetch profile_events with its own self-contained effect so re-renders
  // and realtime subscription updates can't abort or block it
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const today = todayInPacific();
        // Try with lat/lng/emoji/color first
        let { data, error } = await onlyUpcoming(supabase
          .from('profile_events')
          .select('id, name, date, end_date, time, venue, address, description, role, ticket_link, lat, lng, emoji, color'), today)
          .order('date', { ascending: true })
          .limit(50);

        // If those columns don't exist yet, fall back to base columns
        if (error?.code === '42703') {
          ({ data, error } = await onlyUpcoming(supabase
            .from('profile_events')
            .select('id, name, date, end_date, time, venue, address, description, role, ticket_link'), today)
            .order('date', { ascending: true })
            .limit(50));
        }

        if (error) { console.error('profile_events fetch error:', error.message); return; }
        if (cancelled) return;


        const mapped = (data || []).map(ev => ({
          id:          ev.id,
          username:    '@reader',
          role:        ev.role || 'attending',
          title:       ev.name,
          date:        new Date(ev.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          time:        ev.time || '',
          location:    ev.venue || '',
          city:        ev.address || ev.venue || '',
          emoji:       ev.emoji  || (ev.role === 'hosting' ? '🎤' : '🎟️'),
          color:       ev.color  || (ev.role === 'hosting' ? 'var(--brand-rust)' : 'var(--brand-forest)'),
          attendees:   0,
          interested:  0,
          lat:         ev.lat ? parseFloat(ev.lat) : null,
          lng:         ev.lng ? parseFloat(ev.lng) : null,
          desc:        ev.description || '',
          ticket_link: ev.ticket_link || '',
        }));

        setProfileEvents(mapped);
      } catch (err) {
        if (!cancelled) console.error('profile_events fetch failed:', err.message);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // 10-second scroll prompt for non-logged-in visitors
 useEffect(() => gate.startCommunityTimer(), [gate.startCommunityTimer]);

  async function fetchCurrentUser() {
    try {
      const user = await getCurrentUser();
      if (user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('username, display_name, avatar_url')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  }

  async function fetchFreshPicks() {
    setPicksLoading(true);
    try {
      const { data, error } = await supabase
        .from('five_star_reads')
        .select('id, title, author_name, cover_image_url, username, created_at')
        .order('created_at', { ascending: false })
        .limit(6);
      if (error) throw error;
      setFreshPicks((data || []).map(pick => ({
        book: pick.title,
        author: pick.author_name,
        cover: pick.cover_image_url,
        user: pick.username || 'reader',
        time: formatTimeAgo(pick.created_at),
      })));
    } catch {
      setFreshPicks([]);
    } finally {
      setPicksLoading(false);
    }
  }

 async function handlePost({ type, text, selectedBook, spoiler, disappears, pollOptions, imageUrl, bookTags, selectedGenreIds, selectedSubgenreIds }) {
  const user = await getCurrentUser();
  if (!user) { addToast('Please log in to post', 'error'); throw new Error('Not logged in'); }

  const postTypeMap = {
    thought: 'text',
    reading: 'reading_update',
    poll:    'poll',
    image:   'image',
  };

  const expiresAt = disappears === 'never' ? null
    : new Date(Date.now() + (disappears === '12h' ? 43200000 : 86400000)).toISOString();

  // ── If external book, submit to books table as pending ──────────────
   // ── If external book, submit to books table as pending ──────────────
  let resolvedBookId = selectedBook?.id || null;

  if (selectedBook && !selectedBook.id) {
    try {
      resolvedBookId = await findOrCreateBook(
        {
          title:       selectedBook.title,
          author_name: selectedBook.author_name,
          isbn13:      selectedBook.isbn13 || selectedBook.isbn,
        },
        {
          submission_type: 'reader',
          extra: {
            cover_image_url: selectedBook.cover_image_url || null,
            blurb:           selectedBook.blurb || null,
            genres:          selectedBook.genres || [],
          },
        }
      );

      // ── Process tags through edge function ────────────────────────
      //
      // PR5 (docs/genre-subgenre-build-spec.md): tag_type: 'genre' rows are
      // filtered out of the book_tags upsert below -- book_tags stopped
      // being a genre store in this PR. bookTags.genre is now names
      // resolved from the reader's own GenreSubgenrePicker picks
      // (ComposeModal's handleSubmit), not defaulted from the external
      // source's raw metadata the way an earlier version of this file did --
      // that default was unvalidated free text with no reliable name to
      // resolve into a book_genres row, which is exactly why it was never
      // more than "flagged for a decision". The actual book_genres/
      // book_subgenres write is the writeReaderGenrePicks call below,
      // by id, not by re-resolving these names a second time.
      if (resolvedBookId && bookTags) {
        const resolvedTags = await resolveAllTagsToBookTags(bookTags, 'reader');
        for (const bt of resolvedTags) {
          if (!bt.trope_id || bt.tag_type === 'genre') continue;
          // NO added_by here, deliberately. This is the community post
          // composer, not a profile block, and only profile-block tags are
          // removable. See src/lib/tagCredit.js.
          await supabase.from('book_tags').upsert(
            { book_id: resolvedBookId, ...bt },
            { onConflict: 'book_id,trope_id', ignoreDuplicates: true }
          );
        }

        // book_genres/book_subgenres mirror (PR3 Finding 3), same pattern
        // Add5StarReadPopup.jsx and ProfileBlocks.jsx already use -- same
        // ids the reader just picked, no re-lookup by name needed.
        // Secondary to the save itself, so it never blocks posting, but the
        // error is not thrown away either -- see writeReaderGenrePicks's
        // own comment (src/lib/tropeUtils.js). Review on PR 5: silently
        // console.error-ing this left the reader believing their pick
        // landed while the new book sat with no book_genres row -- the same
        // failure CSVUploadModal.jsx already surfaces with a toast
        // (src/components/CSVUploadModal.jsx), so this does too. No "edit
        // it to try again" here, unlike that toast: a reader who just
        // submitted an external book through the composer has no edit
        // access to it (it goes to book_submissions as 'pending', same as
        // any external-book post), so the message says what happened and
        // what it means instead of pointing at an action this reader can't
        // take.
        const { error: genreTablesErr } = await writeReaderGenrePicks(
          resolvedBookId, selectedGenreIds || [], selectedSubgenreIds || []
        );
        if (genreTablesErr) {
          console.error('Composer: book_genres/book_subgenres write failed:', genreTablesErr);
          addToast(
            `"${selectedBook.title}"'s genre didn't save, so it won't be approved until that's fixed, your post still went up.`,
            'error'
          );
        }
      }

      // Book submission record for admin
      await supabase.from('book_submissions').insert({
        submitted_by:  user.id,
        title:         selectedBook.title,
        author_name:   selectedBook.author_name,
        cover_url:     selectedBook.cover_image_url || null,
        genres:        selectedBook.genres || [],
        selected_tags: bookTags ? [
          ...(bookTags.tropes || []),
          ...(bookTags.tone   || []),
          ...(bookTags.genre  || []),
          ...(bookTags.spice ? [bookTags.spice] : []),
        ] : [],
        status: 'pending',
      });
    } catch (err) {
      console.error('Book submission error:', err.message);
    }
  }

  // ── For external books, store metadata inline so card renders it ──
  const bookMeta = (selectedBook && !selectedBook.id) ? {
    title:           selectedBook.title,
    author_name:     selectedBook.author_name,
    cover_image_url: selectedBook.cover_image_url || null,
    source:          selectedBook.source || 'external',
  } : null;

  const { error } = await supabase.from('posts').insert({
    user_id:      user.id,
    post_type:    postTypeMap[type] || 'text',
    content:      text.trim(),
    book_id:      resolvedBookId,
    is_spoiler:   spoiler,
    expires_at:   expiresAt,
    image_url:    imageUrl || null,
    event_data:   bookMeta ? { book_meta: bookMeta } : null,
    poll_options: type === 'poll'
      ? pollOptions.filter(o => o.trim()).map(o => ({ text: o, votes: 0, voters: [] }))
      : null,
  });

  if (error) throw error;
  refetchPosts();
  refetchStats();
  addToast('Posted!', 'success');
}
// Distributes posts round-robin into 3 columns, interleaving column-specific widgets
function buildDesktopColumn(colIndex, columnWidgets) {
  const items = [];
  const colPosts = posts.filter((_, i) => i % 3 === colIndex);
  let widgetIdx = 0;
  colPosts.forEach((post, i) => {
    items.push({ type: 'post', post, index: posts.indexOf(post) });
    // Insert a widget after every 3rd post in this column
    if ((i + 1) % 3 === 0 && widgetIdx < columnWidgets.length) {
      items.push({ type: 'widget', id: columnWidgets[widgetIdx++] });
    }
  });
  // Append remaining widgets at bottom of column
  while (widgetIdx < columnWidgets.length) {
    items.push({ type: 'widget', id: columnWidgets[widgetIdx++] });
  }
  return items;
}

// Build interleaved list: insert a widget after every 3 posts
function buildMasonryItems() {
  if (activeTab === 'events') {
    const items = [];
    posts.forEach((post, i) => {
      if (post.post_type === 'event') {
        items.push({ type: 'post', post, index: i, key: `p-${post.id}` });
      }
    });
    return items;
  }

  const items = [];

  if (isMobile) {
    const remainingWidgets = ['live_activity', 'trending_tropes', 'taste_matches', 'book_clubs', 'hot_topics'];
    items.push({ type: 'widget', id: 'spin',        key: 'w-spin'  });
    items.push({ type: 'widget', id: 'fresh_picks', key: 'w-fresh' });
    let widgetIdx = 0;
    posts.forEach((post, i) => {
      items.push({ type: 'post', post, index: i, key: `p-${post.id}` });
      if ((i + 1) % 3 === 0 && widgetIdx < remainingWidgets.length) {
        items.push({ type: 'widget', id: remainingWidgets[widgetIdx], key: `w-${widgetIdx}` });
        widgetIdx++;
      }
    });
    while (widgetIdx < remainingWidgets.length) {
      items.push({ type: 'widget', id: remainingWidgets[widgetIdx], key: `w-${widgetIdx}` });
      widgetIdx++;
    }

  } else {
    // Desktop: spin/fresh_picks/map are pinned in their own row above
    // masonryItems only handles everything below that
    const remainingWidgets = ['live_activity', 'trending_tropes', 'taste_matches', 'book_clubs', 'hot_topics', 'challenge'];
    let widgetIdx = 0;
    posts.forEach((post, i) => {
      items.push({ type: 'post', post, index: i, key: `p-${post.id}` });
      if ((i + 1) % 3 === 0 && widgetIdx < remainingWidgets.length) {
        items.push({ type: 'widget', id: remainingWidgets[widgetIdx], key: `w-${widgetIdx}` });
        widgetIdx++;
      }
    });
    while (widgetIdx < remainingWidgets.length) {
      items.push({ type: 'widget', id: remainingWidgets[widgetIdx], key: `w-${widgetIdx}` });
      widgetIdx++;
    }
  }

  return items;
}
  function renderWidget(id) {
    switch (id) {
      case 'spin':            return <SpinTheShelfWidget freshPicks={freshPicks} picksLoading={picksLoading} onAddPick={() => setShow5StarModal(true)} />;
      case 'fresh_picks':     return <FreshPicksWidget freshPicks={freshPicks} picksLoading={picksLoading} onAddPick={() => setShow5StarModal(true)} />;
      case 'live_activity':   return <LiveActivityWidget activity={activity} />;
      case 'trending_tropes': return <TrendingTropesWidget tropes={tropes} />;
      case 'taste_matches':   return <TasteMatchesWidget matches={matches} />;
      case 'events_near_me':   return <EventsNearMeWidget events={profileEvents} />;
      case 'hot_topics':      return <HotTopicsWidget />;
     // case 'challenge':       return <CommunityChallengeWidget />;
      default:                return null;
    }
  }

  const masonryItems = buildMasonryItems();
  const filterTabs = [
    { id: 'general', label: '💬 General' },
    { id: 'events',  label: '📍 Events'  },
  ];

  // Extract event posts from the feed to pass real pins to the map widget
  const eventPosts = posts
    .filter(p => p.post_type === 'event')
    .map(p => {
      const ev = p.event_data || p.events || {};
      return {
        id:         p.id,
        username:   `@${p.username}`,
        role:       ev.role       || 'going',
        title:      ev.title      || ev.name      || 'Community Event',
        date:       ev.date       || ev.event_date || '',
        end_date:   ev.end_date   || ev.endDate    || null,
        time:       ev.time       || '',
        location:   ev.location   || ev.venue      || '',
        city:       ev.city       || '',
        emoji:      ev.emoji      || '📅',
        color:      ev.color      || 'var(--brand-rust)',
        attendees:  ev.attendees  || 0,
        interested: ev.interested || 0,
        lat:        ev.lat        || ev.latitude   || null,
        lng:        ev.lng        || ev.longitude  || null,
        desc:       ev.desc       || ev.description || p.content || '',
      };
    })
    // These pins come from the posts feed rather than from profile_events, so
    // the query filter above does not reach them. Without this an event stayed
    // pinned to the map for as long as its post was in the feed.
    .filter(e => isUpcoming(e))
    .filter(e => e.lat && e.lng); // only show events that have coordinates


  // Reader's Corner is not live yet, so only the placeholder renders. The real
  // page below is untouched and comes back by flipping this to false — it was
  // previously rendered blurred underneath the placeholder, where nobody could
  // see it but its full height still gave the route a scrollbar with nothing
  // to scroll to.
  const COMING_SOON = true;

  return (
    <>
    {!COMING_SOON && <div style={{ background: 'var(--brand-paper)', minHeight: '100vh', paddingBottom: isMobile ? 90 : 80, filter: 'blur(6px)',
    pointerEvents: 'none',
    userSelect: 'none' }}>
      <style>{`
        @keyframes fadeIn      { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        @keyframes slideUp     { from { opacity: 0; transform: translate(-50%, 12px); } to { opacity: 1; transform: translate(-50%, 0); } }
        @keyframes slideUpSheet{ from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes modalIn     { from { opacity: 0; transform: scale(0.97) translateY(6px); } to { opacity: 1; transform: none; } }
        @keyframes spin        { to { transform: rotate(360deg); } }

        .masonry-grid { columns: 3; column-gap: 14px; }
        .masonry-item { break-inside: avoid; margin-bottom: 14px; }
        @media (max-width: 1060px) { .masonry-grid { columns: 2; } }
        @media (max-width: 640px)  { .masonry-grid { columns: 1; } }
        @media (max-width: 860px)  { .events-layout { flex-direction: column !important; } .events-map-col { width: 100% !important; position: static !important; } }

        .tab-pill:hover { background: rgba(255,255,255,0.18) !important; }
        .tab-pill-active { background: #fff !important; color: #292524 !important; }
        .compose-trigger:hover { background: #3d6b57 !important; }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ background: 'linear-gradient(175deg, var(--neutral-800) 0%, #3c3532 100%)', padding: isMobile ? '14px 16px 0' : '30px 24px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>

          {/* Title row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-10)', marginBottom: isMobile ? 10 : 14 }}>
            <div>
              <h1 style={{ fontFamily: 'Georgia, serif', fontSize: isMobile ? 20 : 36, fontWeight: 700, color: 'var(--brand-white)', margin: '0 0 2px', letterSpacing: '-0.02em' }}>
                Reader's Corner
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 6, height: 6, borderRadius: 'var(--radius-circle)', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.25)', flexShrink: 0 }} />
                  <span style={{ fontSize: isMobile ? 10 : 13, color: 'var(--neutral-400)' }}>
                    <strong style={{ color: 'var(--brand-white)' }}>{stats.onlineCount?.toLocaleString() || '—'}</strong> online
                  </span>
                </div>
                {!isMobile && (
                  <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>
                    📝 <strong style={{ color: 'var(--brand-white)' }}>{stats.postsThisWeek >= 1000 ? `${(stats.postsThisWeek / 1000).toFixed(1)}k` : (stats.postsThisWeek || 0)}</strong> posts this week
                  </span>
                )}
              </div>
            </div>

            {isMobile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', flexShrink: 0 }}>
                <span style={{ fontSize: 'var(--font-size-xl)', cursor: 'pointer' }}>🔔</span>
                <div
                  style={{ width: 28, height: 28, borderRadius: 'var(--radius-circle)', background: 'var(--brand-rust)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-white)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' }}>
                  {currentUser?.username?.[0]?.toUpperCase() || 'R'}
                </div>
              </div>
            ) : (
              <button
                className="compose-trigger"
                onClick={() => gate.requireAuth(() => setShowCompose(true))}
                style={{ padding: '12px 22px', background: 'var(--brand-forest)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', transition: 'background 0.2s', flexShrink: 0 }}
              >
                💭 Share a thought
              </button>
            )}
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 'var(--space-xs)', overflowX: 'auto', paddingBottom: 1, scrollbarWidth: 'none' }}>
            {filterTabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-pill${activeTab === tab.id ? ' tab-pill-active' : ''}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'events') setEventsPage(1);
                }}
                style={{
                  padding: isMobile ? '8px 14px' : '10px 18px',
                  background: activeTab === tab.id ? 'var(--brand-white)' : 'rgba(255,255,255,0.07)',
                  color: activeTab === tab.id ? 'var(--neutral-800)' : 'var(--neutral-300)',
                  border: 'none', borderRadius: '12px 12px 0 0',
                  fontSize: isMobile ? 11 : 13, fontWeight: activeTab === tab.id ? 700 : 500,
                  cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap', flexShrink: 0,
                  marginBottom: activeTab === tab.id ? -1 : 0,
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTENT AREA ── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '10px 12px' : '20px 16px' }}>
        {postsLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', gap: 'var(--space-14)' }}>
            <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-circle)', border: '3px solid var(--brand-sand)', borderTopColor: 'var(--feedback-warning)', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Loading Reader's Corner...</p>
          </div>

        ) : activeTab === 'events' ? (
   <div className="events-layout" style={{ display: 'flex', gap: 'var(--space-xl)', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>

  {/* Map widget — order:1 on mobile (top), order:2 on desktop (right) */}
  <div className="events-map-col" style={{
    width: isMobile ? '100%' : 380,
    flexShrink: 0,
    position: isMobile ? 'static' : 'sticky',
    top: 20,
    order: isMobile ? 1 : 2,
  }}>
    <EventsNearMeWidget events={profileEvents} />
  </div>

  {/* Event cards — order:2 on mobile (below map), order:1 on desktop (left) */}
  <div style={{ flex: '1 1 0', minWidth: 0, order: isMobile ? 2 : 1 }}>
    {(() => {
      const evPosts    = posts.filter(p => p.post_type === 'event');
      const totalPages = Math.ceil(evPosts.length / EVENTS_PER_PAGE);
      const start      = (eventsPage - 1) * EVENTS_PER_PAGE;
      const paginated  = evPosts.slice(start, start + EVENTS_PER_PAGE);

      if (evPosts.length === 0) {
        return (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--brand-sand)' }}>
            <div style={{ fontSize: 'var(--font-size-h1)', marginBottom: 'var(--space-md)' }}>📍</div>
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>No events posted yet</p>
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Add an event from your profile and it'll appear here</p>
          </div>
        );
      }

      return (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            {paginated.map((post, i) => renderPostCard(post, i, gate.requireAuth))}
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-6)', marginTop: 28, flexWrap: 'wrap' }}>
              <button
                onClick={() => { setEventsPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={eventsPage === 1}
                style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: eventsPage === 1 ? 'default' : 'pointer', background: eventsPage === 1 ? 'var(--neutral-100)' : 'var(--brand-white)', color: eventsPage === 1 ? 'var(--neutral-300)' : 'var(--neutral-800)', border: '1px solid var(--brand-sand)' }}>
                ← Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(n => n === 1 || n === totalPages || Math.abs(n - eventsPage) <= 1)
                .reduce((acc, n, idx, arr) => { if (idx > 0 && n - arr[idx - 1] > 1) acc.push('...'); acc.push(n); return acc; }, [])
                .map((n, i) => n === '...' ? (
                  <span key={`d-${i}`} style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', padding: '0 4px' }}>…</span>
                ) : (
                  <button key={n}
                    onClick={() => { setEventsPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer', background: eventsPage === n ? 'var(--brand-forest)' : 'var(--brand-white)', color: eventsPage === n ? 'var(--brand-white)' : 'var(--neutral-800)', border: eventsPage === n ? 'none' : '1px solid var(--brand-sand)' }}>
                    {n}
                  </button>
                ))}
              <button
                onClick={() => { setEventsPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={eventsPage === totalPages}
                style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: eventsPage === totalPages ? 'default' : 'pointer', background: eventsPage === totalPages ? 'var(--neutral-100)' : 'var(--brand-white)', color: eventsPage === totalPages ? 'var(--neutral-300)' : 'var(--neutral-800)', border: '1px solid var(--brand-sand)' }}>
                Next →
              </button>
              {!isMobile && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', marginLeft: 'var(--space-sm)' }}>
                  {start + 1}–{Math.min(start + EVENTS_PER_PAGE, evPosts.length)} of {evPosts.length}
                </span>
              )}
            </div>
          )}
        </>
      );
    })()}
  </div>

</div>

        ) : isMobile ? (
          /* ── MOBILE: single column ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-10)' }}>
            {masonryItems.map(item => (
              <div key={item.key}>
                {item.type === 'widget'
                  ? renderWidget(item.id)
                  : renderPostCard(item.post, item.index, gate.requireAuth)}
              </div>
            ))}
            {posts.length === 0 && (
              <div style={{ textAlign: 'center', padding: '50px 20px' }}>
                <div style={{ fontSize: 'var(--font-size-h1)', marginBottom: 'var(--space-md)' }}>📚</div>
                <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>
                  {activeTab === 'general' ? 'Nothing here yet' : 'No posts yet'}
                </h3>
                <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 16px' }}>Be the first to share something!</p>
              </div>
            )}
          </div>

) : (
  /* ── DESKTOP: 3 explicit columns (true masonry, no gaps) ── */
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-14)', alignItems: 'start' }}>

    {/* Column 1 — Spin at top, then every 3rd post + widgets */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-14)' }}>
      {renderWidget('spin')}
      {buildDesktopColumn(0, ['live_activity', 'hot_topics']).map((item, i) =>
        item.type === 'widget'
          ? <div key={`c1-w-${i}`}>{renderWidget(item.id)}</div>
          : <div key={`c1-p-${item.post.id}`}>{renderPostCard(item.post, item.index, gate.requireAuth)}</div>
      )}
    </div>

    {/* Column 2 — Fresh Picks at top, then every 3rd post + widgets */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-14)' }}>
      {renderWidget('fresh_picks')}
      {buildDesktopColumn(1, ['trending_tropes', 'taste_matches']).map((item, i) =>
        item.type === 'widget'
          ? <div key={`c2-w-${i}`}>{renderWidget(item.id)}</div>
          : <div key={`c2-p-${item.post.id}`}>{renderPostCard(item.post, item.index, gate.requireAuth)}</div>
      )}
    </div>

    {/* Column 3 — Events Map at top, then every 3rd post + widgets */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-14)' }}>
      {renderWidget('events_near_me')}
      {buildDesktopColumn(2, ['book_clubs', 'challenge']).map((item, i) =>
        item.type === 'widget'
          ? <div key={`c3-w-${i}`}>{renderWidget(item.id)}</div>
          : <div key={`c3-p-${item.post.id}`}>{renderPostCard(item.post, item.index, gate.requireAuth)}</div>
      )}
    </div>

    {posts.length === 0 && (
      <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '50px 0' }}>
        <div style={{ fontSize: 44, marginBottom: 'var(--space-14)' }}>📚</div>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px' }}>Nothing here yet</h3>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 18px' }}>Be the first to share something!</p>
        <button onClick={() => gate.requireAuth(() => setShowCompose(true))}
          style={{ padding: '10px 22px', background: 'var(--brand-forest)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: 'pointer' }}>
          💭 Share a thought
        </button>
      </div>
    )}
  </div>
)}
      </div>

      {/* ── MOBILE FAB ── */}
      {isMobile && (
        <button
          onClick={() => gate.requireAuth(() => setShowCompose(true))}
          style={{
            position: 'fixed', bottom: 84, right: 25, width: 52, height: 52,
            borderRadius: 'var(--radius-circle)', background: 'linear-gradient(135deg, var(--feedback-warning), var(--feedback-warning-ink))',
            color: 'var(--brand-white)', border: 'none', fontSize: 26, cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(217,119,6,0.35)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          }}
        >
          +
        </button>
      )}

      {/* ── MODALS ── */}
      {showCompose && (
        <ComposeModal currentUser={currentUser} onClose={() => setShowCompose(false)} onPost={handlePost} />
      )}
      {show5StarModal && (
        <Add5StarReadPopup onClose={() => setShow5StarModal(false)} onSuccess={() => { fetchFreshPicks(); setShow5StarModal(false); }} />
      )}
      <SignUpGate gate={gate} />
    </div>}
      <ComingSoon
        accent={COMING_SOON_COLOURS.ember}
        maxWidth={1060}
        headlineSize="clamp(32px, 6.4vw, 96px)"
        lineHeight={1.08}
        headline={<>came for the recs,<br />stayed for the <em style={{ fontStyle: 'italic', color: COMING_SOON_COLOURS.ember }}>chaos</em></>}
      />
    </>
  );
}